import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import {
  useParams,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { api } from "@/lib/api";
import { getStoredUser } from "@/lib/auth";
import useCommissionCatalog from "@/hooks/useCommissionCatalog";
import { getQuoteFieldLabel, isCompanyQuoteField, isIssueAgeQuoteField, isProductQuoteField } from "@/lib/quoteFieldUtils";
import { useRunExitGuard } from "@/contexts/RunExitGuardContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";

import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";

// Extracted hooks (MA-ARCH-161)
import { useQuickReference } from "./script-play/hooks/useQuickReference";
import { useReaderFontSize } from "./script-play/hooks/useReaderFontSize";
import { useIsDesktop } from "./script-play/hooks/useMediaQuery";
import { useProspectPanelState } from "./script-play/hooks/useProspectPanelState";

// Extracted components (MA-ARCH-161, MA-ARCH-162)
import { CalculatorPanel } from "./script-play/components/CalculatorPanel";
import { CalendarPanel } from "./script-play/components/CalendarPanel";
import { NotesPanel } from "./script-play/components/NotesPanel";
import { ScriptPlayHeader } from "./script-play/components/ScriptPlayHeader";
import {
  assertLockedPayload,
  buildLockedRunnerPath,
  normalizeEmbeddedRun,
} from "./script-play/utils/embeddedRun";
import {
  CUSTOMER_TOKEN_STATE,
  parseScriptTokens,
  resolveCustomerToken,
} from "./script-play/utils/customerTokens";
import { QuickReferenceRail } from "./script-play/components/QuickReferenceRail";
import { ProspectFilterHeader } from "./script-play/components/ProspectFilterHeader";
import { ReaderFontControls } from "./script-play/components/ReaderFontControls";
import { QuoteSheetPanel } from "./script-play/components/QuoteSheetPanel";
import { ProspectInfoPanel } from "./script-play/components/ProspectInfoPanel";

// Extracted utilities (MA-ARCH-162)
import {
  getValidationError,
  getFieldRenderKey,
  getDefaultFieldSubject,
  getFieldDisplayLabel,
  normalizeNumberInput,
  passesProspectFilter,
  getInputType,
} from "./script-play/utils/fieldUtils";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Separator as UiSeparator } from "@/components/ui/separator";
// (deduped lucide import)

import { toast } from "@/hooks/use-toast";
import AddProspectInline from "@/components/AddProspectInline";
import ProspectInfoModal from "@/components/ProspectInfoModal";
import SetAppointmentDialog from "@/components/appointments/SetAppointmentDialog";
// Icons used in this file (other icons moved to extracted components in MA-ARCH-162)
import {
  ChevronDown,
  Check,
  AlertTriangle,
  FlaskConical,
  Trash2,
  MessageSquareText,
  MessageSquare,
  Calculator,
} from "lucide-react";

// Validation functions moved to ./script-play/utils/fieldUtils.js (MA-ARCH-162)

export default function ScriptPlayPage({ embedded = null, customer = null }) {
  const { id: routeScriptId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // AW-2B3.2. `isEmbedded` is true the moment the prop is supplied at
  // all -- deliberately not "is valid" -- so a malformed object can
  // never be treated as the legacy route and pick up URL authority.
  const isEmbedded = embedded !== null && embedded !== undefined;
  const embeddedRun = useMemo(() => normalizeEmbeddedRun(embedded), [embedded]);
  const embeddedInvalid = isEmbedded && (embeddedRun === null || embeddedRun.invalid);

  // Script identity: the workspace's, or the route's. Never both, and in
  // embedded mode never the route's.
  const id = isEmbedded ? embeddedRun?.scriptId ?? null : routeScriptId;
  const user = getStoredUser();
  const isAgentRole = ["agent", "booking_agent"].includes(user?.role);
  const {
    setActiveRun,
    clearActiveRun,
    setFinalNodeId,
    requestNavigate,
    requestAction,
  } = useRunExitGuard();
  const [script, setScript] = useState(null);
  const [payload, setPayload] = useState(null);

  // Reader font size controls (MA-ARCH-161: extracted to hook)
  const scriptReplyFont = payload?.script?.runner_reply_font_size || 16;
  const { fontDelta: readerFontDelta, setFontDelta: setReaderFontDelta } = useReaderFontSize(scriptReplyFont);

  const [loading, setLoading] = useState(true);

  const [runId, setRunId] = useState(null);
  // AW-2B3.2: embedded-mode failures that must not degrade into an
  // unlocked or unbound render.
  const [embeddedError, setEmbeddedError] = useState(null);

  // AW-2B3.2: query params confer no authority in embedded mode, and
  // never will -- an embedded run's identity comes from the workspace,
  // not the address bar.
  //
  // AW-2C-005 SUPERSEDES THE SECOND HALF OF WHAT THIS COMMENT USED TO
  // SAY. It read "canonical Lead context is AW-2C, and VICIdial lead
  // data must never substitute for it", written when AW-2C was expected
  // to resolve a MissionAssist CRM lead for the call.
  //
  // AW-2C-001 established that no authoritative provider-to-CRM lead
  // correspondence exists: the materialization executor that would
  // create one is absent rather than disabled, `LoadMember` carries no
  // provider lead id, and `provider_native_ref`'s own contract forbids
  // using it as a link between grains. So the operator's accepted
  // direction (AW-2C-002) is that the verified owned VICIdial lead IS
  // the canonical customer context for this surface -- not a substitute
  // for a CRM lead, but the authority itself.
  //
  // What has NOT changed: that context arrives as a server-authorized
  // prop derived from the owned call, never from a query parameter, and
  // never carrying a CRM lead identity. `leadIdFromQuery` below stays
  // null in embedded mode for exactly the original reason.
  const leadIdFromQuery = isEmbedded
    ? null
    : String(searchParams.get("lead_id") || "").trim() || null;
  const appointmentIdFromQuery = isEmbedded
    ? null
    : String(searchParams.get("appointment_id") || "").trim() || null;
  const defaultProspect1FromQuery = isEmbedded
    ? ""
    : String(searchParams.get("p1") || "").trim();

  const hasStartedRun = Boolean(runId);
  const nodeEnterAtRef = useRef(null);

  // Node history for back navigation

  const scrollAfterStartRef = useRef(false);

  const replyCardRef = useRef(null);

  // Scroll control: trigger a scroll AFTER payload changes (ensures DOM is committed).
  const scrollOnNextPayloadRef = useRef(false);

  const [nodeHistory, setNodeHistory] = useState([]);

  // Sandbox mode (testing) - only for admins/super_admins

  // Quote Sheet (Tool Bar)
  const [quoteFields, setQuoteFields] = useState([]);
  const [quoteValues, setQuoteValues] = useState({}); // key: quoteFieldId::prospectId
  const [quoteProspectId, setQuoteProspectId] = useState(null);
  const [quoteOptionIndex, setQuoteOptionIndex] = useState(1); // 1 | 2 | 3

  const {
    companyOptions,
    ensureProducts,
    getCompanyIdByName,
    getProductOptionsByCompanyName,
  } = useCommissionCatalog({ enabled: true, filterByProfiles: true });

  const [commissionProfiles, setCommissionProfiles] = useState([]);
  const [profilesLoading, setProfilesLoading] = useState(false);

  useEffect(() => {
    const loadProfiles = async () => {
      setProfilesLoading(true);
      try {
        const res = await api.get("/commission/profiles");
        setCommissionProfiles(Array.isArray(res.data) ? res.data : []);
      } catch {
        setCommissionProfiles([]);
      } finally {
        setProfilesLoading(false);
      }
    };
    loadProfiles();
  }, []);

  const companyQuoteField = useMemo(
    () => (quoteFields || []).find((f) => isCompanyQuoteField(getQuoteFieldLabel(f))),
    [quoteFields]
  );
  const productQuoteField = useMemo(
    () => (quoteFields || []).find((f) => isProductQuoteField(getQuoteFieldLabel(f))),
    [quoteFields]
  );
  const issueAgeQuoteField = useMemo(
    () => (quoteFields || []).find((f) => isIssueAgeQuoteField(getQuoteFieldLabel(f))),
    [quoteFields]
  );


  const getIssueAgeOptions = useCallback(
    (companyName, productName) => {
      const company = String(companyName || "").trim().toLowerCase();
      const product = String(productName || "").trim().toLowerCase();
      if (!company || !product) return [];
      const matches = (commissionProfiles || []).filter(
        (profile) =>
          String(profile?.company_name || "").trim().toLowerCase() === company &&
          String(profile?.product_name || "").trim().toLowerCase() === product
      );
      const unique = Array.from(
        new Set(
          matches
            .map((profile) => profile.issue_age)
            .filter((age) => age !== undefined && age !== null)
            .map((age) => String(age))
        )
      );
      return unique.sort((a, b) => {
        const na = Number.parseFloat(a);
        const nb = Number.parseFloat(b);
        if (Number.isNaN(na) || Number.isNaN(nb)) return a.localeCompare(b);
        return na - nb;
      });
    },
    [commissionProfiles]
  );

  // show prospect toggle for prospect-scoped script blocks or legacy inserts
  const shouldShowProspectToggle = useMemo(() => {
    if (!payload?.node) return false;
    if (payload.node.scope === "plan_options") return true;

    const blocks = payload.node.your_reply_blocks || [];
    const scriptBlocks = payload.node.script_blocks || [];
    const hasProspectScopedScriptBlocks = (scriptBlocks || []).some(
      (sb) => String(sb?.scope || "") === "prospect",
    );

    if (scriptBlocks.length > 0) {
      return hasProspectScopedScriptBlocks;
    }
    const tokenRe = /\$\((data|quote|calc|agent):([a-zA-Z0-9_\-]+)\)|\$\(option:([123]):([a-zA-Z0-9_\-]+)\)/;

    const legacyHasInserts = blocks.some((b) => {
      if (!b) return false;
      if (b.type === "field" || b.type === "var") return true;
      if (b.type === "text") return tokenRe.test(String(b.text || ""));
      return false;
    });

    return legacyHasInserts;
  }, [payload?.node]);

  const [currentProspectId, setCurrentProspectId] = useState("household");

  const [planProspectId, setPlanProspectId] = useState("household");
  const [planOptionIndex, setPlanOptionIndex] = useState(1); // (kept for legacy; option selection removed from Your Reply header)


  const [quoteAutoSaveStatus, setQuoteAutoSaveStatus] = useState("idle");

  // Initialize from URL param if present
  const [sandboxMode, setSandboxMode] = useState(() => {
    // A Production owned call is never a sandbox run, and a query
    // param must not be able to make it look like one.
    if (isEmbedded) return false;
    return searchParams.get("sandbox") === "true";
  });
  const canUseSandbox = user?.role === "super_admin" || user?.role === "admin";

  // Form fields state
  const [allFields, setAllFields] = useState([]);
  const [fieldValues, setFieldValues] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  // Multi-prospect support
  const [prospectsOpen, setProspectsOpen] = useState(false);
  const [prospectDrafts, setProspectDrafts] = useState([""]);
  const [prospects, setProspects] = useState([]); // run prospects {id,name,sort_order}
  const [lockedProspects, setLockedProspects] = useState([]); // appointment prospects (immutable display)

  const quoteSheetStatus = useMemo(() => {
    const targetProspectId =
      quoteProspectId === "household" ? prospects?.[0]?.id || "" : quoteProspectId;
    const getValue = (field) => {
      if (!field?.id || !targetProspectId) return "";
      return (
        quoteValues[`${field.id}::${targetProspectId}::${quoteOptionIndex}`] || ""
      );
    };
    const companyValue = getValue(companyQuoteField);
    const productValue = getValue(productQuoteField);
    const issueAgeValue = getValue(issueAgeQuoteField);
    const issueAgeRequired = Boolean(issueAgeQuoteField);
    const isReady =
      Boolean(companyValue) &&
      Boolean(productValue) &&
      (issueAgeRequired ? Boolean(issueAgeValue) : true);
    return {
      targetProspectId,
      companyValue,
      productValue,
      issueAgeValue,
      isReady,
    };
  }, [companyQuoteField, issueAgeQuoteField, productQuoteField, prospects, quoteOptionIndex, quoteProspectId, quoteValues]);

  const quoteInputFields = useMemo(() => {
    const base = (quoteFields || [])
      .filter((f) => f.kind === "input")
      .filter((f) => (quoteProspectId === "household" ? !!f.include_in_household : true))
      .slice()
      .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));

    const companyField = base.find((f) => isCompanyQuoteField(getQuoteFieldLabel(f)));
    const productField = base.find((f) => isProductQuoteField(getQuoteFieldLabel(f)));
    const issueAgeField = base.find((f) => isIssueAgeQuoteField(getQuoteFieldLabel(f)));

    if (!companyField || !productField) return base;

    if (!quoteSheetStatus.companyValue) {
      return [companyField];
    }
    if (!quoteSheetStatus.productValue) {
      return [companyField, productField].filter(Boolean);
    }
    if (issueAgeField && !quoteSheetStatus.issueAgeValue) {
      return [companyField, productField, issueAgeField].filter(Boolean);
    }

    const requiredIds = new Set(
      [companyField?.id, productField?.id, issueAgeField?.id].filter(Boolean)
    );
    const remaining = base.filter((f) => !requiredIds.has(f.id));
    return [companyField, productField, ...(issueAgeField ? [issueAgeField] : []), ...remaining].filter(Boolean);
  }, [quoteFields, quoteProspectId, quoteSheetStatus]);


  // Greeting-node start: when prospect info is collected "at start", we prompt here before creating the run.
  const [startProspectInfoOpen, setStartProspectInfoOpen] = useState(false);
  const [startTargetNodeId, setStartTargetNodeId] = useState(null);

  // Prefetch locked appointment prospects (so ProspectInfoModal can lock them BEFORE the run is created)
  useEffect(() => {
    (async () => {
      try {
        if (appointmentIdFromQuery) {
          const ares = await api.get(`/appointments/${appointmentIdFromQuery}`);
          setLockedProspects(ares.data?.prospects || []);
        } else if (leadIdFromQuery) {
          const ares = await api.get(`/appointments/by-lead/${leadIdFromQuery}/prospects`);
          setLockedProspects(ares.data?.prospects || []);
        } else {
          setLockedProspects([]);
        }
      } catch {
        setLockedProspects([]);
      }
    })();
  }, [appointmentIdFromQuery, leadIdFromQuery]);


  const addProspect = useCallback(
    async (name) => {
      if (!runId) return;
      try {
        setLoading(true);
        await api.post(`/runs/${runId}/prospects/add`, { name });
        const pres = await api.get(`/runs/${runId}/prospects`);
        setProspects(pres.data || []);
        toast({ title: "Prospect added" });
      } catch (e) {
        toast({
          title: "Failed to add prospect",
          description: e?.response?.data?.detail || "Please try again.",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    },
    [runId, toast],
  );
  // Applies-to selection per rendered instance.
  // For household-scoped fields: key = field_id
  // For prospect-scoped fields: key = field_id::instance_prospect_id (one per prospect copy)
  const [subjectByKey, setSubjectByKey] = useState({});

  const [editProspectsOpen, setEditProspectsOpen] = useState(false);
  const [prospectEdits, setProspectEdits] = useState({}); // prospect_id -> name

  // Prospect Info filter (side panel only)
  const [prospectInfoFilter, setProspectInfoFilter] = useState("household");

  const [prospectPanelView, setProspectPanelView] = useState("prospect_info");

  const quoteToolId = "quote_sheet";

  const selectedIconClass =
    "bg-indigo-500/90 text-white ring-indigo-400/20 hover:bg-indigo-500";

  // Quick Reference: Calendar + Notepad (MA-ARCH-161: extracted to hook)
  const quickRef = useQuickReference(runId);
  const { calendarSelected, setCalendarSelected, notesText, setNotesText } = quickRef;

  const [setApptOpen, setSetApptOpen] = useState(false);
  const [setApptLead, setSetApptLead] = useState(null);

  const setApptMode = appointmentIdFromQuery ? "schedule" : "convert";
  const setApptLeadPayload = setApptLead || (leadIdFromQuery ? { id: leadIdFromQuery } : null);

  useEffect(() => {
    let active = true;
    const loadLead = async () => {
      if (!appointmentIdFromQuery && !leadIdFromQuery) {
        if (active) setSetApptLead(null);
        return;
      }
      try {
        if (appointmentIdFromQuery) {
          const res = await api.get(`/appointments/${appointmentIdFromQuery}`);
          const appt = res.data || {};
          let snapshot = appt?.lead_snapshot || null;
          if (snapshot && !snapshot.id && appt?.lead_id) {
            snapshot = { ...snapshot, id: appt.lead_id };
          }
          if (snapshot?.id || snapshot?.full_name || snapshot?.email) {
            if (active) setSetApptLead(snapshot);
            return;
          }
          if (appt?.lead_id) {
            const leadRes = await api.get(`/leads/${appt.lead_id}`);
            if (active) setSetApptLead(leadRes.data || null);
            return;
          }
        }
        if (leadIdFromQuery) {
          const leadRes = await api.get(`/leads/${leadIdFromQuery}`);
          if (active) setSetApptLead(leadRes.data || null);
        }
      } catch {
        if (active) setSetApptLead(null);
      }
    };

    loadLead();
    return () => {
      active = false;
    };
  }, [appointmentIdFromQuery, leadIdFromQuery]);

  const idleIconClass =
    "bg-white/10 text-slate-100 ring-white/10 hover:bg-white/15";

  // Prospect panel collapsed state (MA-ARCH-161: extracted to hook)
  const { collapsed: prospectPanelCollapsed, setCollapsed: setProspectPanelCollapsed } = useProspectPanelState();

  // Desktop breakpoint (MA-ARCH-161: extracted to hook)
  const isDesktop = useIsDesktop();

  const quickRefCollapsed = isDesktop ? prospectPanelCollapsed : false;

  const quickRefPanelWidth = quickRefCollapsed ? 76 : 460;

  const [autoSaveStatus, setAutoSaveStatus] = useState("idle"); // idle, saving, saved
  const lastSavedToastAtRef = useRef(0);

  const autoSaveTimerRef = useRef(null);
  const lastSavedValuesRef = useRef({});

  // Validation warning dialog
  const [validationWarningOpen, setValidationWarningOpen] = useState(false);
  const [pendingOutcome, setPendingOutcome] = useState(null);

  // (Removed) calculator auto-focus when opened
  // The calculator should not steal focus or trigger browser auto-scroll.
  // useEffect(() => {
  //   if (prospectPanelView !== "placeholder_2") return;
  //   // Let the panel render, then focus
  //   setTimeout(() => {
  //     const el = document.querySelector(
  //       '[data-testid="quick-reference-calculator"]',
  //     );
  //     if (el) el.focus();
  //   }, 0);
  // }, [prospectPanelView]);

  const [validationIssues, setValidationIssues] = useState([]);

  const logEvent = async (event) => {
    if (!runId) return;
    try {
      await api.post("/analytics/events", { run_id: runId, ...event });
    } catch (e) {
      // silent
    }
  };

  const openProspectEdit = () => {
    const next = {};
    (prospects || []).forEach((p) => {
      next[p.id] = p.name || "";
    });
    setProspectEdits(next);
    setEditProspectsOpen(true);
  };

  const saveProspectEdits = async () => {
    if (!runId) return;
    const updates = (prospects || [])
      .map((p) => ({
        id: p.id,
        oldName: p.name || "",
        newName: (prospectEdits[p.id] || "").trim(),
      }))

      .filter((x) => x.newName && x.newName !== x.oldName);

    if (updates.length === 0) {
      setEditProspectsOpen(false);
      return;
    }

    try {
      setLoading(true);
      for (const u of updates) {
        await api.put(`/run-prospects/${u.id}`, { name: u.newName });
      }
      const pres = await api.get(`/runs/${runId}/prospects`);
      setProspects(pres.data || []);
      toast({ title: "Prospects updated" });
      setEditProspectsOpen(false);
    } catch (e) {
      toast({
        title: "Failed to update prospects",
        description: e?.response?.data?.detail || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const startStallTimer = (nodeId) => {
    if (stallTimerRef.current) {
      clearTimeout(stallTimerRef.current);
      stallTimerRef.current = null;
    }
    stalledThisNodeRef.current = false;
    nodeEnterAtRef.current = Date.now();

    stallTimerRef.current = setTimeout(async () => {
      if (stalledThisNodeRef.current) return;
      stalledThisNodeRef.current = true;
      await logEvent({
        event_type: "stall",
        node_id: nodeId,
        seconds_on_node: 90,
      });
    }, 90000);
  };

  const stopStallTimer = () => {
    if (stallTimerRef.current) {
      clearTimeout(stallTimerRef.current);
      stallTimerRef.current = null;
    }
  };

  const stallTimerRef = useRef(null);
  const stalledThisNodeRef = useRef(false);

  const fetchScript = async () => {
    const res = await api.get(`/scripts/${id}`);
    setScript(res.data);
  };

  const fetchAllFields = async () => {
    try {
      const res = await api.get(`/scripts/${id}/fields`);
      setAllFields(res.data);
    } catch (e) {
      // silent - fields are optional
    }
  };

  // Load Quote Sheet field definitions (global per script)
  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const res = await api.get(`/scripts/${id}/quote_fields`);
        setQuoteFields(res.data || []);
      } catch {
        setQuoteFields([]);
      }
    })();
  }, [id]);

  // Load Data Field definitions (global per script)
  useEffect(() => {
    if (!id) return;
    fetchAllFields();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Load existing Quote Sheet values for the current run
  useEffect(() => {
    if (!runId) return;
    (async () => {
      try {
        const res = await api.get(`/runs/${runId}/quote_values`);
        const next = {};
        (res.data || []).forEach((v) => {
          const opt = v.option_index || 1;
          next[`${v.quote_field_id}::${v.prospect_id}::${opt}`] = v.value || "";
        });
        setQuoteValues(next);
      } catch {
        // ignore
      }
    })();
  }, [runId]);

  const fetchRunData = async (rid) => {
    try {
      const res = await api.get(`/runs/${rid}/data`);
      const values = {};
      res.data.forEach((d) => {
        const subject = d.subject_id || "household";
        values[`${d.field_id}::${subject}`] = d.value;
      });
      setFieldValues(values);
    } catch (e) {
      // silent
    }
  };

  /**
   * AW-2B3.2 -- the single seam every runner-payload fetch goes through.
   *
   * Legacy route: unchanged. The request is exactly what it was, so the
   * existing unlocked behaviour of `/scripts/:id/play` is preserved.
   *
   * Embedded (Production workspace): `run_id` is appended to EVERY
   * Script and node payload request, and the response must come back
   * `version_locked: true`. Both halves matter. Without the query the
   * backend serves live Script content, which is the version-lock
   * bypass this slice exists to close; without the assertion a backend
   * that silently stopped locking would render live content that looks
   * identical. There is deliberately no retry-without-run_id path --
   * failing closed is the only safe answer, because the alternative is
   * showing an agent a Script that is not the one their run locked.
   */
  const fetchRunnerPayload = useCallback(
    async (path) => {
      if (!isEmbedded) return api.get(path);
      if (!embeddedRun || embeddedRun.invalid || !embeddedRun.runId) {
        throw new Error("aw2b3_embedded_run_required");
      }
      const res = await api.get(buildLockedRunnerPath(path, embeddedRun.runId));
      assertLockedPayload(res?.data);
      return res;
    },
    [isEmbedded, embeddedRun]
  );

  const fetchStart = async () => {
    setLoading(true);
    try {
      // Greeting node is shown first without creating a run.
      setRunId(null);
      setPayload(null);
      setProspects([]);
      setSubjectByKey({});
      setNodeHistory([]);

      const res = await fetchRunnerPayload(`/scripts/${id}/run/start`);
      setPayload(res.data);

      // No run exists yet, so we do not log analytics events here.
      startStallTimer(res.data.node.id);
    } finally {
      setLoading(false);
    }
  };

  // Start run on mount / when script id changes

  // (Prospect info is collected based on script settings; we keep query param values as defaults)
  useEffect(() => {
    // noop; defaults are derived from query params
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // AW-2B3.2. In embedded mode the run already exists and is
    // authoritative; a `?run_id=` in the address bar must not be able to
    // point the workspace at a different run.
    const continueRunId = isEmbedded
      ? embeddedRun?.runId ?? null
      : searchParams.get("run_id");
    if (continueRunId) {
      (async () => {
        try {
          setLoading(true);
          await fetchScript();
          await fetchAllFields();

          setRunId(continueRunId);
          setActiveRun({
            runId: continueRunId,
            sandbox: false,
            collectAt: script?.collect_prospect_info_at || "start",
            defaultProspects: defaultProspect1FromQuery ? [defaultProspect1FromQuery] : [],
          });

          const pres = await api.get(`/runs/${continueRunId}/prospects`);
          setProspects(pres.data || []);

          await fetchRunData(continueRunId);

          // Load quote values
          try {
            const qv = await api.get(`/runs/${continueRunId}/quote_values`);
            const next = {};
            (qv.data || []).forEach((v) => {
              const opt = v.option_index || 1;
              next[`${v.quote_field_id}::${v.prospect_id}::${opt}`] = v.value || "";
            });
            setQuoteValues(next);
          } catch {
            // ignore
          }

          // Resume at final node (product decision)
          const rd = await api.get(`/runs/${continueRunId}`);
          const finalNodeId = rd?.data?.run?.final_node_id;
          if (finalNodeId) {
            const nodeRes = await fetchRunnerPayload(`/nodes/${finalNodeId}/run`);
            setPayload(nodeRes.data);
          } else {
            const res = await fetchRunnerPayload(`/scripts/${id}/run/start`);
            setPayload(res.data);
          }

          setProspectsOpen(false);
        } catch (e) {
          // AW-2B3.2: the legacy fallback re-enters the no-run greeting
          // flow, which clears runId. That is right for the route and
          // wrong for the workspace, where the run is the authority --
          // so embedded mode surfaces the failure instead.
          if (isEmbedded) {
            setEmbeddedError(e);
          } else {
            fetchStart();
          }
        } finally {
          setLoading(false);
        }
      })();
      return;
    }

    fetchStart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Keep global run guard state in sync. A run is "active" after it is created (runId exists).
  useEffect(() => {
    if (runId) {
      setActiveRun({
        runId,
        sandbox: sandboxMode,
        collectAt: payload?.script?.collect_prospect_info_at || "start",
        defaultProspects: defaultProspect1FromQuery ? [defaultProspect1FromQuery] : [],
      });
    } else {
      clearActiveRun();
    }
  }, [
    clearActiveRun,
    defaultProspect1FromQuery,
    payload?.script?.collect_prospect_info_at,
    runId,
    sandboxMode,
    setActiveRun,
  ]);

  // Default selected prospect for Quote Sheet
  useEffect(() => {
    if (!prospects || prospects.length === 0) return;
    setQuoteProspectId((prev) => prev || prospects[0].id);
  }, [prospects]);

  useEffect(() => {
    if (!companyQuoteField?.id) return;
    const names = new Set();
    Object.entries(quoteValues || {}).forEach(([key, value]) => {
      if (!key.startsWith(`${companyQuoteField.id}::`)) return;
      if (value) names.add(String(value));
    });
    names.forEach((name) => {
      const id = getCompanyIdByName(name);
      if (id) ensureProducts(id);
    });
  }, [companyQuoteField?.id, quoteValues, ensureProducts, getCompanyIdByName]);

  // Default Prospect toggle to Prospect 1 for Plan Options or Prospect-scoped nodes.
  useEffect(() => {
    if (!prospects || prospects.length === 0) return;
    if (!(payload?.node?.scope === "plan_options" || shouldShowProspectToggle)) {
      setPlanProspectId("household");
      return;
    }
    setPlanProspectId((prev) => {
      if (prev !== "household" && (prospects || []).some((p) => p.id === prev)) return prev;
      return prospects[0].id;
    });
  }, [payload?.node?.id, payload?.node?.scope, prospects, shouldShowProspectToggle]);

  // Default current prospect selector
  useEffect(() => {
    if (!prospects || prospects.length === 0) {
      setCurrentProspectId("household");
      return;
    }
    setCurrentProspectId((prev) => {
      if (prev === "household") return prev;
      if ((prospects || []).some((p) => p.id === prev)) return prev;
      return prospects[0].id;
    });
  }, [prospects]);

  // Reset selected quote option when switching the "For" prospect/household selector
  useEffect(() => {
    setQuoteOptionIndex(1);
  }, [quoteProspectId]);

  // Quote Sheet autosave (matches Prospect Info/Data Fields pattern: 2s debounce)
  const quoteAutoSaveTimeoutRef = useRef(null);
  useEffect(() => {
    if (!runId) return;
    if (!quoteProspectId) return;

    if (quoteAutoSaveTimeoutRef.current) {
      clearTimeout(quoteAutoSaveTimeoutRef.current);
    }

    quoteAutoSaveTimeoutRef.current = setTimeout(async () => {
      try {
        setQuoteAutoSaveStatus("saving");

        const entries = [];
        (quoteFields || []).forEach((f) => {
          if (f.kind !== "input") return;
          const key = `${f.id}::${quoteProspectId}::${quoteOptionIndex}`;
          if (Object.prototype.hasOwnProperty.call(quoteValues, key)) {
            entries.push({
              quote_field_id: f.id,
              prospect_id: quoteProspectId,
              option_index: quoteOptionIndex,
              value: quoteValues[key] || "",
            });
          }
        });

        if (entries.length > 0) {
          await api.post(`/runs/${runId}/quote_values`, { entries });
        }

        setQuoteAutoSaveStatus("saved");
        setTimeout(() => setQuoteAutoSaveStatus("idle"), 1000);
      } catch {
        setQuoteAutoSaveStatus("error");
        setTimeout(() => setQuoteAutoSaveStatus("idle"), 1500);
      }
    }, 2000);

    return () => {
      if (quoteAutoSaveTimeoutRef.current) {
        clearTimeout(quoteAutoSaveTimeoutRef.current);
        quoteAutoSaveTimeoutRef.current = null;
      }
    };
  }, [quoteFields, quoteOptionIndex, quoteProspectId, quoteValues, runId]);

  const setQuoteValue = useCallback(
    (fieldId, prospectId, optionIndex, value) => {
      setQuoteValues((prev) => {
        const next = {
          ...prev,
          [`${fieldId}::${prospectId}::${optionIndex}`]: value,
        };
        if (companyQuoteField?.id && fieldId === companyQuoteField.id) {
          if (productQuoteField?.id) {
            next[`${productQuoteField.id}::${prospectId}::${optionIndex}`] = "";
          }
          if (issueAgeQuoteField?.id) {
            next[`${issueAgeQuoteField.id}::${prospectId}::${optionIndex}`] = "";
          }
        }
        if (productQuoteField?.id && fieldId === productQuoteField.id && issueAgeQuoteField?.id) {
          next[`${issueAgeQuoteField.id}::${prospectId}::${optionIndex}`] = "";
        }
        return next;
      });
    },
    [companyQuoteField?.id, issueAgeQuoteField?.id, productQuoteField?.id],
  );

  const computeQuoteFormula = useCallback(
    (formula, prospectId, optionIndexOverride = null, mode = "prospect") => {
      // formula: [{type:'field', source:'quote'|'data'|'calculated', id, label}, {type:'op', op:'+'|'-'|'*'|'/'}, {type:'number', value} ...]
      // mode: 'prospect' | 'household'
      if (!Array.isArray(formula) || formula.length === 0) return "";

      const resolveFieldNumber = (t, pid, opts = {}) => {
        if (!t || t.type !== "field") return 0;
        const effectivePid = pid || prospectId;
        const preferProspectOnly = Boolean(opts.preferProspectOnly);

        // Quote inputs
        if (t.source === "quote") {
          const opt = optionIndexOverride || quoteOptionIndex;
          const householdKey = `${t.id}::household::${opt}`;
          const key = `${t.id}::${effectivePid}::${opt}`;

          const rawProspect = quoteValues[key];
          const rawHousehold = quoteValues[householdKey];

          const raw = preferProspectOnly
            ? rawProspect || ""
            : (rawProspect !== undefined && rawProspect !== "" ? rawProspect : undefined) ??
              (rawHousehold !== undefined && rawHousehold !== "" ? rawHousehold : "") ??
              "";

          const n = parseFloat(String(raw).replace(/,/g, ""));
          return Number.isFinite(n) ? n : 0;
        }

        // Data fields (run_data)
        if (t.source === "data") {
          // Data fields may be household-scoped or prospect-scoped.
          const householdKey = `${t.id}::household`;
          const prospectKey = `${t.id}::${effectivePid}`;

          const rawProspect = fieldValues[prospectKey];
          const rawHousehold = fieldValues[householdKey];

          const raw = preferProspectOnly
            ? rawProspect || ""
            : (rawProspect !== undefined && rawProspect !== "" ? rawProspect : undefined) ??
              (rawHousehold !== undefined && rawHousehold !== "" ? rawHousehold : "") ??
              "";

          const n = parseFloat(String(raw).replace(/,/g, ""));
          return Number.isFinite(n) ? n : 0;
        }

        // Nested calculated fields
        if (t.source === "calculated") {
          const cf = (quoteFields || []).find((x) => x.id === t.id);
          if (!cf) return 0;
          const raw = computeQuoteFormula(cf.formula || [], effectivePid, optionIndexOverride, mode);
          const n = parseFloat(String(raw).replace(/,/g, ""));
          return Number.isFinite(n) ? n : 0;
        }

        return 0;
      };

      const toks = formula
        .map((t) => {
          if (t.type === "field") {
            // In household mode, treat prospect-scoped fields as summed across prospects.
            if (mode === "household") {
              const householdKey = t.source === "data" ? `${t.id}::household` : null;
              const hasHouseholdValue = householdKey ? fieldValues[householdKey] !== undefined && fieldValues[householdKey] !== "" : false;

              // If there is no household value, this is effectively a prospect-scoped input.
              if (t.source === "data" && !hasHouseholdValue) {
                const sum = (prospects || []).reduce((acc, p) => {
                  const n = resolveFieldNumber(t, p.id, { preferProspectOnly: true });
                  return acc + (Number.isFinite(n) ? n : 0);
                }, 0);
                return sum;
              }
            }

            return resolveFieldNumber(t);
          }

          if (t.type === "sum_all") {
            const sum = (prospects || []).reduce((acc, p) => {
              const n = resolveFieldNumber({ ...t, type: "field" }, p.id, { preferProspectOnly: true });
              return acc + (Number.isFinite(n) ? n : 0);
            }, 0);
            return sum;
          }
          if (t.type === "number") {
            const n = parseFloat(String(t.value || "0").replace(/,/g, ""));
            return Number.isFinite(n) ? n : 0;
          }
          if (t.type === "op") return t.op;
          if (t.type === "lparen") return "(";
          if (t.type === "rparen") return ")";
          return 0;
        })
        .filter((x) => x !== 0 || x === 0);


      // Convert tokens to expression string and evaluate
      const expr = toks
        .map((x) => (typeof x === "number" ? String(x) : x))
        .join(" ")
        .replace(/\s+\)/g, ")")
        .replace(/\(\s+/g, "(");

      try {
        const val = Function(`return (${expr})`)();
        if (!Number.isFinite(val)) return "Error";
        return String(val);
      } catch {
        return "Error";
      }
    },
    [fieldValues, prospects, quoteFields, quoteOptionIndex, quoteValues],
  );

  const renderTextWithTokens = useCallback(
    (text) => {
      const src = String(text || "");
      if (!src) return null;

      // AW-2C-005. The parser lives in `script-play/utils/customerTokens`
      // so the merge-field contract is provable as values -- this page
      // cannot be imported under Jest, which is the same reason
      // `embeddedRun.js` sits beside it. The pattern gained one
      // alternative (`customer`); every pre-existing source parses to
      // exactly the shape it always did.
      const parts = parseScriptTokens(src);

      if (!parts.length) return null;

      const isPlanOptionsNode = payload?.node?.scope === "plan_options";
      const effectiveProspectId = planProspectId;
      const effectiveOptionIndex = isPlanOptionsNode ? planOptionIndex : quoteOptionIndex;


      const resolveOptionTokenValue = (t) => {
        const placeholder = "—";
        const pid = effectiveProspectId || "household";
        const opt = Number(t?.optionIndex) || 1;

        const field = (quoteFields || []).find((q) => q.id === t.id);
        const fieldType = String(field?.field_type || "text");

        // Household: sum numeric/currency across prospects; otherwise show per-prospect pills.
        if (pid === "household") {
          if (fieldType === "number") {
            const sum = (prospects || []).reduce((acc, p) => {
              const raw = quoteValues[`${t.id}::${p.id}::${opt}`] || "";
              const n = parseFloat(String(raw).replace(/,/g, ""));
              return acc + (Number.isFinite(n) ? n : 0);
            }, 0);

            // Match existing household display behavior in Quote Sheet panel
            return Number.isFinite(sum) ? String(sum) : "0";
          }

          // Non-numeric: show all prospect values as labeled pills
          const values = (prospects || [])
            .map((p) => {
              const v = quoteValues[`${t.id}::${p.id}::${opt}`];
              if (v === undefined || v === "") return null;
              return { name: p.name || "Prospect", value: String(v) };
            })
            .filter(Boolean);

          if (!values.length) return placeholder;

          return (
            <span className="inline-flex flex-wrap items-center gap-1">
              {values.map((it, idx) => (
                <span
                  key={idx}
                  className="inline-flex rounded-md bg-white/10 px-2 py-1 text-slate-100"
                >
                  {it.name} - {it.value}
                </span>
              ))}
            </span>
          );
        }

        // Prospect-selected: show that prospect's value
        const key = `${t.id}::${pid}::${opt}`;
        const v = quoteValues[key];
        return v === undefined || v === "" ? placeholder : String(v);
      };

      const resolveTokenValue = (t) => {
        const placeholder = "—";
        const pid = effectiveProspectId || "household";

        if (t.source === "data") {
          const f = (allFields || []).find((x) => x.id === t.id);
          if (!f) return placeholder;
          const v = fieldValues[`${f.id}::${pid}`];
          return v === undefined || v === "" ? placeholder : String(v);
        }


        if (t.source === "quote") {
          const key = `${t.id}::${pid}::${effectiveOptionIndex || 1}`;
          const v = quoteValues[key];
          return v === undefined || v === "" ? placeholder : String(v);
        }

        if (t.source === "calc") {
          const cf = (quoteFields || []).find((x) => x.id === t.id);
          if (!cf) return placeholder;
          const raw = computeQuoteFormula(
            cf.formula || [],
            pid,
            effectiveOptionIndex || 1,
            pid === "household" ? "household" : "prospect",
          );
          return !raw || raw === "Error" ? placeholder : String(raw);
        }

        if (t.source === "agent") {
          const u = getStoredUser();
          if (!u) return placeholder;

          const fullName = String(u.name || u.full_name || "").trim();
          const nameParts = fullName.split(/\s+/).filter(Boolean);

          if (t.id === "full_name") {
            return fullName ? fullName : placeholder;
          }
          if (t.id === "first_name") {
            const first = String(u.first_name || "").trim() || nameParts[0] || "";
            return first ? first : placeholder;
          }


          if (t.id === "last_name") {
            const last = String(u.last_name || "").trim() || nameParts.slice(1).join(" ");
            return last ? last : placeholder;
          }

          return placeholder;
        }

        // AW-2C-005 -- the owned call's customer.
        //
        // A SEPARATE BRANCH FROM `agent`, AND NEVER A FALLBACK TO IT.
        // `$(agent:first_name)` is the person making the call;
        // `$(customer:first_name)` is the person receiving it. Resolving
        // one through the other would put the agent's own name in the
        // greeting addressed to the customer, which is the single worst
        // thing this feature could do. There is no code path between
        // them.
        //
        // No fabrication either: an absent value yields the pending or
        // unavailable treatment below, never an invented name and never
        // the em-dash placeholder the other sources use. "Hi —," read
        // aloud is worse than a visible gap, and "Hi Jordan," when
        // Jordan is the agent is worse than both.
        if (t.source === "customer") {
          const resolved = resolveCustomerToken(t.id, customer);

          if (resolved.state === CUSTOMER_TOKEN_STATE.UNKNOWN_FIELD) {
            return placeholder;
          }

          if (resolved.state === CUSTOMER_TOKEN_STATE.VALUE) {
            return resolved.value;
          }

          if (resolved.state === CUSTOMER_TOKEN_STATE.PENDING) {
            // Subtle, wordless, and sized so the line does not reflow
            // noticeably when the name lands. Announced to assistive
            // technology, which cannot see a shimmer.
            return (
              <span
                className="inline-block h-3 w-16 animate-pulse rounded bg-white/20 align-middle"
                aria-busy="true"
                aria-label="Loading customer information"
                data-testid={`script-customer-token-pending-${t.id}`}
              />
            );
          }

          // Unavailable, or present-but-empty for this field. Clear,
          // short, and non-blocking: the agent reads past it and keeps
          // using the Script.
          return (
            <span
              className="text-[11px] italic text-amber-300/80"
              data-testid={`script-customer-token-unavailable-${t.id}`}
            >
              not available
            </span>
          );
        }

        return placeholder;
      };

      return (
        <span className="whitespace-pre-wrap">
          {parts.map((p, idx) => {
            if (p.type === "text") return <React.Fragment key={idx}>{p.value}</React.Fragment>;
            const v = p.type === "option_token" ? resolveOptionTokenValue(p) : resolveTokenValue(p);
            if (p.type === "option_token") {
              if (React.isValidElement(v)) {
                return (
                  <span key={idx} className="mx-0.5">
                    {v}
                  </span>
                );
              }
              return (
                <span key={idx} className="mx-0.5 inline-flex rounded-md bg-white/10 px-2 py-1 text-slate-100">
                  {v}
                </span>
              );
            }
            return (
              <span key={idx} className="mx-0.5 inline-flex rounded-md bg-white/10 px-2 py-1 text-slate-100">
                {v}
              </span>
            );
          })}
        </span>
      );
    },
    [
      payload?.node?.scope,
      planProspectId,
      planOptionIndex,
      currentProspectId,
      quoteOptionIndex,
      allFields,
      fieldValues,
      quoteValues,
      quoteFields,
      computeQuoteFormula,
      // AW-2C-005. The snapshot itself, not its fields.
      //
      // Token rendering is a pure function of this, so a customer
      // arriving after mount re-renders the node's text and nothing
      // else -- no remount, and no reset of the run, the current node,
      // the collected answers or the navigation history, none of which
      // key off this value.
      //
      // `useOwnedCallCustomer` returns its state object directly, so
      // the identity changes only when that state actually changes.
      // Listing `customer?.loading`/`.tokens` instead would be no more
      // precise and would read as an unsatisfied dependency to
      // exhaustive-deps, which cannot see through an optional chain.
      customer,
    ]
  );

  const scrollReplyCardIntoPosition = useCallback(() => {
    if (!replyCardRef.current) return;
    const gap = 9; // exact requested gap: header bottom -> card top

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const header =
          document.querySelector('[data-testid="app-header"]') ||
          document.querySelector("header") ||
          document.querySelector('[data-testid="top-nav"]');
        if (!header || !replyCardRef.current) return;

        const scrollEl = document.querySelector('[data-testid="app-main"]');

        if (scrollEl && scrollEl.scrollHeight > scrollEl.clientHeight) {
          // When scrolling inside AppShell, compute positions relative to the scroll container
          // so we don't over-reserve an extra "frozen" region under the header.
          const containerTop = scrollEl.getBoundingClientRect().top;
          const cardTopInContainer =
            replyCardRef.current.getBoundingClientRect().top - containerTop;
          const targetTop = Math.max(
            0,
            scrollEl.scrollTop + cardTopInContainer - gap,
          );
          scrollEl.scrollTo({ top: targetTop, behavior: "auto" });
        } else {
          const headerBottom = header.getBoundingClientRect().bottom;
          const cardTopViewport =
            replyCardRef.current.getBoundingClientRect().top;
          const delta = cardTopViewport - (headerBottom + gap);
          const nextTop = Math.max(0, window.scrollY + delta);
          window.scrollTo(0, nextTop);
        }
      });
    });
  }, []);

  const startRunAndGo = useCallback(
    async ({
      targetNodeId,
      prospect_names = [],
      selected_lead_id = null,
      lead_name = "",
    }) => {
      // AW-2B3.2. The Production workspace already holds its run from
      // POST /telephony/script/run/start, which bound it to the
      // authoritative owned call. Creating a second run here -- through
      // the legacy browser-supplied-script_id route, no less -- would
      // both duplicate the run and reintroduce the exact authority the
      // workspace path exists to remove. Structurally unreachable, not
      // merely unused.
      if (isEmbedded) {
        throw new Error("aw2b3_legacy_run_start_forbidden_when_embedded");
      }
      // Create lead first if needed (so the run starts linked, if desired)
      let leadId = selected_lead_id || leadIdFromQuery || null;
      const leadName = String(lead_name || "").trim();

      if (!leadId && leadName) {
        const lr = await api.post("/leads", { full_name: leadName });
        leadId = lr.data?.id;
      }

      const runRes = await api.post("/analytics/runs/start", {
        script_id: id,
        lead_id: leadId || "",
        appointment_id: appointmentIdFromQuery || null,
        sandbox: sandboxMode,
        prospects: (prospect_names || []).map((n) => String(n || "").trim()).filter(Boolean),
      });

      const newRunId = runRes.data.run_id;
      setRunId(newRunId);
      setActiveRun({
        runId: newRunId,
        sandbox: sandboxMode,
        collectAt: payload?.script?.collect_prospect_info_at || "start",
        defaultProspects:
          (prospect_names || []).map((n) => String(n || "").trim()).filter(Boolean) ||
          (defaultProspect1FromQuery ? [defaultProspect1FromQuery] : []),
      });

      // Fetch prospects list:
      // - Always fetch run prospects (these contain the run_prospect IDs used for prospect-scoped blocks)
      // - Separately, fetch appointment prospects for locking in ProspectInfoModal
      try {
        const pres = await api.get(`/runs/${newRunId}/prospects`);
        setProspects(pres.data || []);
      } catch {
        setProspects([]);
      }

      try {
        if (appointmentIdFromQuery) {
          const ares = await api.get(`/appointments/${appointmentIdFromQuery}`);
          const ap = ares.data || {};
          setLockedProspects(ap.prospects || []);
        } else if (leadId) {
          const ares = await api.get(`/appointments/by-lead/${leadId}/prospects`);
          setLockedProspects(ares.data?.prospects || []);
        } else {
          setLockedProspects([]);
        }
      } catch {
        setLockedProspects([]);
      }

      const nodeRes = await fetchRunnerPayload(`/nodes/${targetNodeId}/run`);
      scrollOnNextPayloadRef.current = true;
      setPayload(nodeRes.data);
      startStallTimer(nodeRes.data.node.id);

      // Start run on target node
      try {
        await api.post("/analytics/events", {
          run_id: newRunId,
          event_type: "visit",
          node_id: targetNodeId,
          seconds_on_node: 0,
        });
      } catch {
        // ignore
      }
    },
    [
      defaultProspect1FromQuery,
      id,
      leadIdFromQuery,
      payload?.script?.collect_prospect_info_at,
      sandboxMode,
      setActiveRun,
      appointmentIdFromQuery,
    ],
  );

  useEffect(() => {
    if (!payload?.node?.id) return;
    if (!scrollOnNextPayloadRef.current) return;

    scrollOnNextPayloadRef.current = false;
    scrollReplyCardIntoPosition();
  }, [payload?.node?.id, scrollReplyCardIntoPosition]);

  const onPickOption = async (opt) => {
    try {
      const now = Date.now();
      const enteredAt = nodeEnterAtRef.current || now;
      const secondsOnNode = Math.max(0, Math.round((now - enteredAt) / 1000));

      stopStallTimer();

      // Auto-save before transitioning to next node
      if (runId && Object.keys(fieldValues).length > 0) {
        await saveData(true);
      }

      const isGreetingNode =
        payload?.node?.node_type === "greeting" ||
        payload?.script?.greeting_node_id === payload?.node?.id;

      // If we're on Greeting and there's no run yet, starting depends on script settings.
      if (isGreetingNode && !runId) {
        const collectAt = payload?.script?.collect_prospect_info_at || "start";

        if (collectAt === "start") {
          setStartTargetNodeId(opt.target_node_id);
          setStartProspectInfoOpen(true);
          return;
        }

        await startRunAndGo({
          targetNodeId: opt.target_node_id,
          prospect_names: [],
          selected_lead_id: leadIdFromQuery,
          lead_name: "",
        });
        return;
      }

      // Save current node to history before navigating
      if (payload?.node?.id) {
        setNodeHistory((prev) => [...prev, payload.node.id]);
      }

      const res = await fetchRunnerPayload(`/nodes/${opt.target_node_id}/run`);
      // Ensure we scroll AFTER React commits the new node content
      scrollOnNextPayloadRef.current = true;
      setPayload(res.data);

      startStallTimer(res.data.node.id);
      
      // Phase 2A: Log option_click event with option_id (primary event for analytics)
      await logEvent({
        event_type: "option_click",
        node_id: payload?.node?.id,
        from_node_id: payload?.node?.id,
        to_node_id: opt.target_node_id,
        option_id: opt.id,
        seconds_on_node: secondsOnNode,
      });
      
      // Keep transition event for backwards compatibility
      await logEvent({
        event_type: "transition",
        node_id: opt.target_node_id,
        from_node_id: payload?.node?.id,
        to_node_id: opt.target_node_id,
        seconds_on_node: secondsOnNode,
      });
      
      // Log visit to new node
      await logEvent({
        event_type: "visit",
        node_id: opt.target_node_id,
        seconds_on_node: 0,
      });

    } catch (e) {
      toast({
        title: "Could not load next step",
        description: e?.response?.data?.detail || "Please try again.",
        variant: "destructive",
      });
    }
  };

  // Go back to previous node
  const onGoBack = async () => {
    if (nodeHistory.length === 0) return;

    try {
      stopStallTimer();

      // Get the last node from history
      const prevNodeId = nodeHistory[nodeHistory.length - 1];

      // Remove it from history

      setNodeHistory((prev) => prev.slice(0, -1));

      // Load the previous node
      const res = await fetchRunnerPayload(`/nodes/${prevNodeId}/run`);
      // Ensure we scroll AFTER React commits the new node content
      scrollOnNextPayloadRef.current = true;
      setPayload(res.data);

      startStallTimer(res.data.node.id);
    } catch (e) {
      toast({
        title: "Could not go back",
        description: e?.response?.data?.detail || "Please try again.",
        variant: "destructive",
      });
    }
  };

  // Fields for the side panel
  const uniqueFields = useMemo(() => {
    // Backwards compatible: old fields may not have scope; treat as household.
    return (allFields || []).map((f) => ({
      ...f,
      scope: f.scope || "household",
    }));
  }, [allFields]);

  // Fields for current node (from payload.node_fields)
  const currentNodeFields = useMemo(() => {
    return payload?.node_fields || [];
  }, [payload]);

  // Update field value (syncs between inline and panel) with validation
  const updateFieldValue = useCallback(
    (field, value, subjectId = "household") => {
      const key = `${field.id}::${subjectId}`;
      setFieldValues((prev) => ({ ...prev, [key]: value }));

      // Validate on change if field was already touched (required = once per run)
      if (touchedFields[field.id]) {
        const error = getValidationError(field, value);
        setFieldErrors((prev) => ({ ...prev, [field.id]: error }));
      }
    },
    [touchedFields],
  );

  // Mark field as touched and validate on blur
  const handleFieldBlur = useCallback(
    (field, subjectId) => {
      setTouchedFields((prev) => ({ ...prev, [field.id]: true }));
      const subject = subjectId || "household";
      const value = fieldValues[`${field.id}::${subject}`] || "";
      const error = getValidationError(field, value);
      setFieldErrors((prev) => ({ ...prev, [field.id]: error }));
    },
    [fieldValues],
  );

  // (Removed) label-based sync helpers (deprecated)

  // Save all field data (silent mode for auto-save)
  const saveData = useCallback(
    async (silent = false) => {
      if (!runId) {
        if (!silent) toast({ title: "Run not started" });
        return false;
      }

      const entries = Object.entries(fieldValues)
        .filter(([_, v]) => v !== undefined && v !== "")


        .map(([k, value]) => {
          const [field_id, subject_id] = k.split("::");
          return {
            field_id,
            subject_id: subject_id || "household",
            value: String(value),
          };
        });

      if (entries.length === 0) {
        if (!silent) toast({ title: "No data to save" });
        return false;
      }

      // Check if values changed since last save
      const currentHash = JSON.stringify(entries);
      const lastHash = JSON.stringify(
        Object.entries(lastSavedValuesRef.current)
          .filter(([_, v]) => v !== undefined && v !== "")
          .map(([k, value]) => {
            const [field_id, subject_id] = k.split("::");
            return {
              field_id,
              subject_id: subject_id || "household",
              value: String(value),
            };
          }),
      );

      if (currentHash === lastHash) {
        // No changes, skip save
        return true;
      }

      if (!silent) setIsSaving(true);
      if (silent) setAutoSaveStatus("saving");

      try {
        await api.post(`/runs/${runId}/data`, { entries });
        lastSavedValuesRef.current = { ...fieldValues };

        if (!silent) {
          toast({ title: "Prospect data saved" });
        } else {
          const now = Date.now();
          // Avoid spamming toasts while typing: at most once every 10 seconds
          if (now - lastSavedToastAtRef.current >= 10_000) {
            lastSavedToastAtRef.current = now;
            toast({ title: "Saved" });
          }
        }
        return true;
      } catch (e) {
        if (!silent) {
          toast({
            title: "Save failed",
            description: e?.response?.data?.detail || "Please try again.",
            variant: "destructive",
          });
        }
        setAutoSaveStatus("idle");
        return false;
      } finally {
        if (!silent) setIsSaving(false);
      }
    },
    [runId, fieldValues],
  );

  // Manual save handler
  const onSaveData = async () => {
    await saveData(false);
  };

  // Validate all fields and return issues
  const validateAllFields = useCallback(() => {
    const issues = [];
    uniqueFields.forEach((field) => {
      // Required is considered satisfied if ANY subject has a non-empty value (A)
      const valuesForField = Object.entries(fieldValues)
        .filter(([k, _]) => k.startsWith(`${field.id}::`))
        .map(([_, v]) => String(v || "").trim());

      const anyValue = valuesForField.find((v) => v !== "") || "";
      const error = getValidationError(field, anyValue);
      if (error) {
        issues.push({ field: field.label, error, fieldId: field.id });
      }
    });
    return issues;
  }, [uniqueFields, fieldValues]);

  // Handle outcome button click with validation
  const handleOutcomeClick = useCallback(
    async (outcomeKey, outcomeLabel) => {
      if (!runId || !payload?.node?.id) {
        toast({ title: "Run not started" });
        return;
      }

      setFinalNodeId(payload.node.id);

      const issues = validateAllFields();

      if (issues.length > 0) {
        // Mark all fields as touched to show errors
        const touched = {};
        const errors = {};
        uniqueFields.forEach((f) => {
          touched[f.id] = true;
          const valuesForField = Object.entries(fieldValues)
            .filter(([k, _]) => k.startsWith(`${f.id}::`))
            .map(([_, v]) => String(v || "").trim());
          const anyValue = valuesForField.find((v) => v !== "") || "";
          const error = getValidationError(f, anyValue);
          if (error) errors[f.id] = error;
        });
        setTouchedFields(touched);
        setFieldErrors(errors);

        // Show warning dialog
        setValidationIssues(issues);
        setPendingOutcome({ key: outcomeKey, label: outcomeLabel });
        setValidationWarningOpen(true);
        return;
      }

      // No issues, proceed with ending run
      await completeOutcome(outcomeKey);
    },
    [runId, payload, setFinalNodeId, validateAllFields, uniqueFields, fieldValues],
  );

  // Complete outcome (after validation passes or user confirms)
  const completeOutcome = useCallback(
    async (outcomeKey) => {
      try {
        await saveData(true);
        
        // Phase 2A: Log session_end event before ending the run
        await logEvent({
          event_type: "session_end",
          node_id: payload.node.id,
          seconds_on_node: 0,
        });
        
        await api.post("/analytics/runs/end", {
          run_id: runId,
          outcome: outcomeKey,
          final_node_id: payload.node.id,
        });

        // If this run was launched from a Lead, convert it to an Appointment.
        try {
          await api.post(`/runs/${runId}/convert-lead`);
        } catch {
          // non-blocking
        }

        toast({ title: "Outcome saved" });
        clearActiveRun();
        setRunId(null);
      } catch (e) {
        toast({
          title: "Could not save outcome",
          description: e?.response?.data?.detail || "Please try again.",
          variant: "destructive",
        });
      }
    },
    [clearActiveRun, runId, payload, saveData, setRunId, logEvent],
  );


  // Auto-save: trigger after 2 seconds of inactivity
  useEffect(() => {
    if (!runId || Object.keys(fieldValues).length === 0) return;

    // Clear existing timer
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }

    // Set new timer for auto-save
    autoSaveTimerRef.current = setTimeout(() => {
      saveData(true);
    }, 2000);

    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, [fieldValues, runId, saveData]);


  // Keep final node id updated so disposition can include it (best-effort)
  useEffect(() => {
    if (payload?.node?.id) {
      setFinalNodeId(payload.node.id);
    }
  }, [payload, setFinalNodeId]);

  const subjectOptions = useMemo(() => {
    // Household is always available. Prospects are added per run.
    return [{ id: "household", name: "Household" }, ...prospects];
  }, [prospects]);

  // headerType removed

  const fieldTypeLabels = {
    text: "Text",
    phone: "Phone",
    email: "Email",
    number: "Number",
    textarea: "Long text",
    checkbox: "Checkbox",
    dropdown: "Dropdown",
  };

  // Render a single field input with validation.
  // If field.scope === "prospects", pass instanceProspectId to render a per-prospect copy.
  const renderFieldInput = (
    field,
    isInline = false,
    instanceProspectId = null,
  ) => {
    const scope = field.scope || "household";

    const renderKey =
      scope === "prospects" && instanceProspectId
        ? `${field.id}::${instanceProspectId}`
        : field.id;

    // Default applies-to:
    // - household scoped: household
    // - prospects scoped: this instance's prospect
    const defaultSubject =
      scope === "prospects" && instanceProspectId
        ? instanceProspectId
        : "household";
    const selectedSubject = subjectByKey[renderKey] ?? defaultSubject;

    // Side-panel "Show" filter only affects what is displayed in the panel.
    const passesPanelFilter = isInline
      ? true
      : prospectInfoFilter === "all"
        ? true
        : selectedSubject === prospectInfoFilter;

    if (!passesPanelFilter) return null;

    const value = fieldValues[`${field.id}::${selectedSubject}`] || "";
    const prefix = isInline ? "inline" : "panel";
    const error = touchedFields[field.id] ? fieldErrors[field.id] : null;
    const hasError = !!error;

    const displayLabel =
      String(field?.label ?? field?.name ?? field?.title ?? "").trim() ||
      "Field";
    const typeHint = fieldTypeLabels[field.field_type] || field.field_type || "Field"; // kept for internal logic but not displayed

    if (field.field_type === "checkbox") {
      return (
        <div className="space-y-1" key={renderKey}>
          <div className="flex items-start justify-between gap-2">
            <label
              className={`min-w-0 whitespace-normal break-words text-sm text-slate-200/80 leading-snug ${hasError ? "text-rose-300" : ""}`}
              data-testid={`${prefix}-field-label-${field.id}`}
            >
              {displayLabel}
              {field.required && <span className="ml-1 text-rose-400">*</span>}
            </label>

            <div className="shrink-0">
              <Select
                value={selectedSubject}
                onValueChange={(val) => {
                  setSubjectByKey((prev) => ({ ...prev, [renderKey]: val }));
                }}
              >
                <SelectTrigger
                  className="h-9 w-[160px] bg-white/5 text-slate-100 ring-1 ring-white/10"
                  data-testid={`${prefix}-subject-select-${field.id}${scope === "prospects" && instanceProspectId ? `-${instanceProspectId}` : ""}`}
                >
                  <SelectValue placeholder="Applies to" />
                </SelectTrigger>
                <SelectContent>
                  {subjectOptions.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Checkbox
              checked={value === "true"}
              onCheckedChange={(checked) => {
                updateFieldValue(
                  field,
                  checked ? "true" : "false",
                  selectedSubject,
                );
              }}
              onBlur={() => handleFieldBlur(field, selectedSubject)}
              data-testid={`${prefix}-checkbox-${field.id}${scope === "prospects" && instanceProspectId ? `-${instanceProspectId}` : ""}`}
            />
          </div>

          {hasError && (
            <div
              className="text-xs text-rose-300"
              data-testid={`${prefix}-field-error-${field.id}`}
            >
              {error}
            </div>
          )}
        </div>
      );
    }

    if (field.field_type === "dropdown") {
      const options = field.options || [];
      return (
        <div className="space-y-1" key={renderKey}>
          <div className="flex items-start justify-between gap-2">
            <label
              className={`min-w-0 whitespace-normal break-words text-sm text-slate-200/80 leading-snug ${hasError ? "text-rose-300" : ""}`}
              data-testid={`${prefix}-field-label-${field.id}`}
            >
              {displayLabel}
              {field.required && <span className="ml-1 text-rose-400">*</span>}
            </label>

            <div className="shrink-0">
              <Select
                value={selectedSubject}
                onValueChange={(val) => {
                  setSubjectByKey((prev) => ({ ...prev, [renderKey]: val }));
                }}
              >
                <SelectTrigger
                  className="h-9 w-[160px] bg-white/5 text-slate-100 ring-1 ring-white/10"
                  data-testid={`${prefix}-subject-select-${field.id}${scope === "prospects" && instanceProspectId ? `-${instanceProspectId}` : ""}`}
                >
                  <SelectValue placeholder="Applies to" />
                </SelectTrigger>
                <SelectContent>
                  {subjectOptions.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <Select
            value={value}
            onValueChange={(val) => {
              updateFieldValue(field, val, selectedSubject);
            }}
          >
            <SelectTrigger
              className={
                "bg-white/5 text-slate-100 ring-1 " +
                (hasError
                  ? "ring-rose-500/50 focus:ring-rose-500"
                  : "ring-white/10")
              }
              data-testid={`${prefix}-field-select-${field.id}`}
            >
              <SelectValue placeholder={`Select ${displayLabel.toLowerCase()}`} />
            </SelectTrigger>
            <SelectContent>
              {options.map((opt, idx) => (
                <SelectItem
                  key={idx}
                  value={opt}
                  data-testid={`${prefix}-field-option-${field.id}-${idx}`}
                >
                  {opt}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {hasError && (
            <p
              className="text-xs text-rose-400"
              data-testid={`${prefix}-field-error-${field.id}${scope === "prospects" && instanceProspectId ? `-${instanceProspectId}` : ""}`}
            >
              {error}
            </p>
          )}
        </div>
      );
    }

    if (field.field_type === "textarea") {
      return (
        <div className="space-y-1" key={renderKey}>
          <div className="flex items-start justify-between gap-2">
            <label
              className={`min-w-0 whitespace-normal break-words text-sm text-slate-200/80 leading-snug ${hasError ? "text-rose-300" : ""}`}
              data-testid={`${prefix}-field-label-${field.id}`}
            >
              {displayLabel}
              {field.required && <span className="ml-1 text-rose-400">*</span>}
            </label>

            <div className="shrink-0">
              <Select
                value={selectedSubject}
                onValueChange={(val) => {
                  setSubjectByKey((prev) => ({ ...prev, [renderKey]: val }));
                }}
              >
                <SelectTrigger
                  className="h-9 w-[160px] bg-white/5 text-slate-100 ring-1 ring-white/10"
                  data-testid={`${prefix}-subject-select-${field.id}${scope === "prospects" && instanceProspectId ? `-${instanceProspectId}` : ""}`}
                >
                  <SelectValue placeholder="Applies to" />
                </SelectTrigger>
                <SelectContent>
                  {subjectOptions.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <Textarea
            value={value}
            onChange={(e) => {
              updateFieldValue(field, e.target.value, selectedSubject);
            }}
            onBlur={() => handleFieldBlur(field, selectedSubject)}
            placeholder={`Enter ${displayLabel.toLowerCase()}`}
            className={
              "min-h-[80px] bg-white/5 text-slate-100 placeholder:text-slate-400/70 ring-1 " +
              (hasError
                ? "ring-rose-500/50 focus:ring-rose-500"
                : "ring-white/10")
            }
            data-testid={`${prefix}-field-input-${field.id}${scope === "prospects" && instanceProspectId ? `-${instanceProspectId}` : ""}`}
          />
          {hasError && (
            <p
              className="text-xs text-rose-400"
              data-testid={`${prefix}-field-error-${field.id}${scope === "prospects" && instanceProspectId ? `-${instanceProspectId}` : ""}`}
            >
              {error}
            </p>
          )}
        </div>
      );
    }

    // Default: text, phone, email, number
    const inputType =
      field.field_type === "email"
        ? "email"
        : field.field_type === "phone"
          ? "tel"
          : field.field_type === "number"
            ? "text"
            : "text";

    return (
      <div className="space-y-1" key={renderKey}>
        <div className="flex items-start justify-between gap-2">
          <label
            className={`min-w-0 whitespace-normal break-words text-sm text-slate-200/80 leading-snug ${hasError ? "text-rose-300" : ""}`}
            data-testid={`${prefix}-field-label-${field.id}`}
          >
            {displayLabel}
            {field.required && <span className="ml-1 text-rose-400">*</span>}
            {/* type hint removed */}
          </label>

          <div className="shrink-0">
            <Select
            value={selectedSubject}
            onValueChange={(val) => {
              setSubjectByKey((prev) => ({ ...prev, [renderKey]: val }));
            }}
          >
            <SelectTrigger
              className="h-9 w-[160px] bg-white/5 text-slate-100 ring-1 ring-white/10"
              data-testid={`${prefix}-subject-select-${field.id}`}
            >
              <SelectValue placeholder="Applies to" />
            </SelectTrigger>
            <SelectContent>
              {subjectOptions.map((s) => (
                <SelectItem key={s.id} value={s.id}>
                  {s.name}
                </SelectItem>
              ))}
            </SelectContent>
            </Select>
          </div>
        </div>

        <Input
          type={inputType}
          inputMode={field.field_type === "number" ? "decimal" : undefined}
          value={value}
          onChange={(e) => {
            const next =
              field.field_type === "number"
                ? e.target.value.replace(/[^0-9.\-+]/g, "")
                : e.target.value;
            updateFieldValue(field, next, selectedSubject);
          }}
          onBlur={() => handleFieldBlur(field, selectedSubject)}
          placeholder={`Enter ${displayLabel.toLowerCase()}`}
          className={
            "bg-white/5 text-slate-100 placeholder:text-slate-400/70 ring-1 " +
            (hasError
              ? "ring-rose-500/50 focus:ring-rose-500"
              : "ring-white/10")
          }
          data-testid={`${prefix}-field-input-${field.id}${scope === "prospects" && instanceProspectId ? `-${instanceProspectId}` : ""}`}
        />
        {hasError && (
          <p
            className="text-xs text-rose-400"
            data-testid={`${prefix}-field-error-${field.id}${scope === "prospects" && instanceProspectId ? `-${instanceProspectId}` : ""}`}
          >
            {error}
          </p>
        )}
      </div>
    );
  };

  // AW-2B3.2 -- fail closed, never fall through to a live render.
  //
  // Reached when the workspace supplied a half-formed `embedded`, when a
  // locked payload could not be fetched, or when the backend answered
  // without `version_locked`. Showing the agent *some* Script would be
  // worse than showing none: the whole point of the run binding is that
  // what they read is the publication their run locked.
  if (isEmbedded && (embeddedInvalid || embeddedError)) {
    return (
      <div
        className="flex min-h-[12rem] flex-col items-center justify-center gap-2 px-4 py-8 text-center"
        data-testid="workspace-script-runner-error"
        role="alert"
      >
        <p className="text-sm text-slate-300">
          The Script for this call could not be loaded.
        </p>
        <p className="text-xs text-slate-500">
          Nothing is shown rather than a Script this call may not be running.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="script-play-page">
      {/* Sandbox Mode Banner */}
      {sandboxMode && (
        <div
          className="flex items-center justify-center gap-2 rounded-xl border border-amber-400/30 bg-amber-500/15 px-4 py-2 text-amber-300"
          data-testid="sandbox-banner"
        >
          <FlaskConical className="h-4 w-4" />
          <span className="text-sm font-medium">
            Sandbox Mode — This run will not count toward analytics
          </span>
        </div>
      )}

      {/* Header Controls (MA-ARCH-162: orchestrated via ScriptPlayHeader) */}
      <ScriptPlayHeader
        script={script}
        sandboxMode={sandboxMode}
        onSandboxToggle={(checked) => {
          if (hasStartedRun) {
            requestAction(() => {
              setSandboxMode(checked);
              fetchStart(checked);
            });
            return;
          }
          setSandboxMode(checked);
          fetchStart(checked);
        }}
        canUseSandbox={canUseSandbox}
        hasStartedRun={hasStartedRun}
        isAgentRole={isAgentRole}
        onRestart={() => {
          scrollAfterStartRef.current = true;
          if (hasStartedRun) {
            requestAction(() => fetchStart(sandboxMode));
            return;
          }
          fetchStart(sandboxMode);
        }}
        onEdit={(e) => {
          if (isAgentRole) return;
          e.preventDefault();
          if (hasStartedRun) {
            requestNavigate(`/app/scripts/${id}/edit`);
            return;
          }
          // AW-2B3.2: the workspace must not navigate the agent out of
          // a live call into the Script editor.
          if (isEmbedded) return;
          navigate(`/app/scripts/${id}/edit`);
        }}
        onEndRun={() => {
          const to = (() => {
            const ptId = script?.product_type_id || script?.playbook_id;
            return ptId ? `/app/playbooks/${ptId}` : "/app";
          })();
          if (hasStartedRun) {
            requestNavigate(to);
            return;
          }
          if (isEmbedded) return;
          navigate(to);
        }}
      />


      <ProspectInfoModal
        open={startProspectInfoOpen}
        onOpenChange={(v) => {
          if (!v) {
            setStartProspectInfoOpen(false);
            setStartTargetNodeId(null);
          }
        }}
        title="Prospect Info"
        defaultProspectNames={defaultProspect1FromQuery ? [defaultProspect1FromQuery] : [""]}
        defaultLeadId={leadIdFromQuery}
        defaultLeadLabel={defaultProspect1FromQuery}
        lockedProspects={lockedProspects || null}
        requireLead={true}
        onSubmit={async (info) => {
          const target = startTargetNodeId;
          if (!target) return;
          await startRunAndGo({
            targetNodeId: target,
            prospect_names: info.prospect_names,
            selected_lead_id: info.selected_lead_id,
            lead_name: info.lead_name,
          });
          setStartProspectInfoOpen(false);
          setStartTargetNodeId(null);
        }}
      />

      <SetAppointmentDialog
        lead={setApptLeadPayload}
        open={setApptOpen}
        onOpenChange={setSetApptOpen}
        onConverted={() => setSetApptOpen(false)}
        mode={setApptMode}
        appointmentId={appointmentIdFromQuery || null}
      />

      {/* Validation Warning Dialog */}
      <Dialog
        open={validationWarningOpen}
        onOpenChange={setValidationWarningOpen}
      >
        <DialogContent
          className="glass border-white/10"
          data-testid="validation-warning-dialog"
        >
          <DialogHeader>
            <DialogTitle
              className="flex items-center gap-2 text-amber-300"
              data-testid="validation-warning-title"
            >
              <AlertTriangle className="h-5 w-5" />
              Missing or Invalid Information
            </DialogTitle>

            <DialogDescription
              className="text-slate-200/70"
              data-testid="validation-warning-description"
            >
              The following fields have issues. Would you like to fix them or
              continue anyway?
            </DialogDescription>
          </DialogHeader>
          <div
            className="space-y-2 rounded-xl border border-amber-500/20 bg-amber-500/10 p-3"
            data-testid="validation-issues-list"
          >
            {validationIssues.map((issue, idx) => (
              <div key={idx} className="flex items-start gap-2 text-sm">
                <span className="text-amber-300">•</span>
                <span className="text-slate-200">
                  <strong>{issue.field}:</strong> {issue.error}
                </span>
              </div>
            ))}
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="secondary"
              className="rounded-full bg-indigo-500/90 text-white hover:bg-indigo-500"
              onClick={() => setValidationWarningOpen(false)}
              data-testid="validation-fix-button"
            >
              Go back and fix
            </Button>
            <Button
              className="rounded-full bg-amber-500/90 text-amber-950 hover:bg-amber-500"
              onClick={async () => {
                setValidationWarningOpen(false);
                if (pendingOutcome) {
                  await completeOutcome(pendingOutcome.key);
                  setPendingOutcome(null);
                }
              }}
              data-testid="validation-continue-button"
            >
              Continue anyway
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Prospects Setup Dialog (required before creating a run) */}
      <Dialog
        open={prospectsOpen}
        onOpenChange={(open) => {
          // allow closing only after a run exists
          if (!runId) return;
          setProspectsOpen(open);
        }}
      >
        <DialogContent
          className="glass border-white/10"
          data-testid="prospects-dialog"
        >
          <DialogHeader>
            <DialogTitle
              className="text-slate-100"
              data-testid="prospects-dialog-title"
            >
              Prospect(s) on this call
            </DialogTitle>
            <DialogDescription
              className="text-slate-200/70"
              data-testid="prospects-dialog-description"
            >
              Add at least 1 prospect. Household will always be available as a
              separate category.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            {prospectDrafts.map((name, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <Input
                  value={name}
                  onChange={(e) => {
                    const v = e.target.value;
                    setProspectDrafts((prev) =>
                      prev.map((p, i) => (i === idx ? v : p)),
                    );
                  }}
                  placeholder={`Prospect ${idx + 1} name`}
                  className="bg-white/5 text-slate-100 placeholder:text-slate-400/70 ring-1 ring-white/10"
                  data-testid={`prospect-name-input-${idx}`}
                />
                <Button
                  variant="secondary"
                  size="icon"
                  className="rounded-full bg-white/10 text-rose-300 ring-1 ring-white/10 hover:bg-white/15 disabled:opacity-50"
                  onClick={() =>
                    setProspectDrafts((prev) =>
                      prev.filter((_, i) => i !== idx),
                    )
                  }
                  disabled={prospectDrafts.length <= 1}
                  data-testid={`prospect-remove-${idx}`}
                  title="Remove"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}

            <Button
              variant="secondary"
              className="rounded-full bg-indigo-500/90 text-white hover:bg-indigo-500"
              onClick={() => setProspectDrafts((prev) => [...prev, ""])}
              data-testid="prospect-add-row"
            >
              + Prospect
            </Button>
          </div>

          <DialogFooter>
            <Button
              className="rounded-full bg-indigo-500/90 text-white hover:bg-indigo-500"
              onClick={async () => {
                // Save prospects and start run in a single backend call
                const names = prospectDrafts
                  .map((n) => n.trim())
                  .filter(Boolean);
                if (names.length === 0) {
                  toast({
                    title: "Add at least one prospect",
                    variant: "destructive",
                  });
                  return;
                }

                try {
                  const shouldScrollAfterStart = scrollAfterStartRef.current;

                  setLoading(true);
                  await fetchScript();
                  await fetchAllFields();

                  const clientRunId = crypto.randomUUID();
                  const runRes = await api.post("/analytics/runs/start", {
                    script_id: id,
                    lead_id: leadIdFromQuery || "",
                    appointment_id: appointmentIdFromQuery || null,
                    sandbox: sandboxMode,
                    client_run_id: clientRunId,
                    prospects: names,
                  });

                  setRunId(runRes.data.run_id);
                  setActiveRun({
                    runId: runRes.data.run_id,
                    sandbox: sandboxMode,
                    collectAt: "start",
                    defaultProspects: names,
                  });

                  const pres = await api.get(
                    `/runs/${runRes.data.run_id}/prospects`,
                  );
                  setProspects(pres.data || []);

                  const res = await fetchRunnerPayload(`/scripts/${id}/run/start`);

                  // Clear payload first so restarting triggers the node-change scroll reliably
                  setPayload(null);
                  setFieldValues({});
                  setSubjectByKey({});
                  setNodeHistory([]);
                  await new Promise((r) => setTimeout(r, 0));

                  setPayload(res.data);

                  // Only auto-scroll after explicit navigation actions (option/back/restart)
                  if (shouldScrollAfterStart) {
                    scrollAfterStartRef.current = false;
                    scrollOnNextPayloadRef.current = true;
                  }

                  startStallTimer(res.data.node.id);
                  await api.post("/analytics/events", {
                    run_id: runRes.data.run_id,
                    event_type: "visit",
                    node_id: res.data.node.id,
                    seconds_on_node: 0,
                    client_event_id: `visit:start:${runRes.data.run_id}:${res.data.node.id}`,
                  });

                  setProspectsOpen(false);
                } catch (e) {
                  toast({
                    title: "Failed to start",
                    description:
                      e?.response?.data?.detail || "Please try again.",
                    variant: "destructive",
                  });
                } finally {
                  setLoading(false);
                }
              }}
              data-testid="prospects-save-and-start"
            >
              Submit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Outcome Card removed (Forced Disposition flow now handled by modal) */}
      <div style={{ display: "none" }} data-testid="runner-outcome-card-removed" />

      {loading ? (
        <div className="muted" data-testid="script-play-loading-text">
          Loading…
        </div>
      ) : !payload ? (
        <div className="muted" data-testid="script-play-missing-text">
          Unable to load this script.
        </div>
      ) : (
        <div
          className={


            "grid gap-6 " +
            (quickRefCollapsed
              ? "lg:grid-cols-[minmax(0,1fr)_76px]"
              : "lg:grid-cols-[minmax(0,1fr)_460px]")
          }
          style={{ justifyItems: "stretch" }}
          data-testid="script-play-grid"
        >
          {/* Edit Prospect Names Dialog */}
          <Dialog open={editProspectsOpen} onOpenChange={setEditProspectsOpen}>
            <DialogContent
              className="glass border-white/10"
              data-testid="edit-prospects-dialog"
            >
              <DialogHeader>
                <DialogTitle
                  className="text-slate-100"
                  data-testid="edit-prospects-title"
                >
                  Edit prospect names
                </DialogTitle>
                <DialogDescription
                  className="text-slate-200/70"
                  data-testid="edit-prospects-description"
                >
                  Updating names will immediately reflect in Applies to
                  dropdowns.
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-3">
                {(prospects || []).map((p) => (
                  <div key={p.id} className="space-y-1">
                    <label className="text-xs text-slate-400">
                      Current: {p.name}
                    </label>
                    <Input
                      value={prospectEdits[p.id] ?? p.name ?? ""}
                      onChange={(e) => {
                        const v = e.target.value;
                        setProspectEdits((prev) => ({ ...prev, [p.id]: v }));
                      }}
                      className="bg-white/5 text-slate-100 placeholder:text-slate-400/70 ring-1 ring-white/10"
                      data-testid={`edit-prospect-input-${p.id}`}
                    />
                  </div>
                ))}
              </div>

              <DialogFooter>
                <Button
                  className="rounded-full bg-indigo-500/90 text-white hover:bg-indigo-500"
                  onClick={saveProspectEdits}
                  data-testid="edit-prospects-save"
                >
                  Save
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Main content - Your Reply + Options */}
          <div className="min-w-0 space-y-4" data-testid="runner-main-col">
            <Card
              ref={replyCardRef}
              className="glass"
              data-testid="runner-reply-card"
            >
              <CardHeader>
                <CardTitle className="text-slate-100" data-testid="runner-reply-title">
                  <div className="grid w-full grid-cols-[1fr_auto_1fr] items-center gap-3">
                    <div className="flex items-center gap-2" data-testid="runner-reply-title-left">
                      <MessageSquareText className="h-5 w-5 text-indigo-400" />
                      Your Reply
                    </div>

                    {payload?.node?.scope === "plan_options" || shouldShowProspectToggle ? (
                      <div className="flex items-center justify-center gap-3" data-testid="runner-your-reply-header-toggles">
                        <div className="flex flex-wrap items-center gap-2 rounded-full bg-white/5 p-1 ring-1 ring-white/10" data-testid="runner-plan-prospect-toggle">
                          <Button
                            variant="secondary"
                            className={
                              "h-8 rounded-full px-3 " +
                              (planProspectId === "household"
                                ? "bg-indigo-500/90 text-white hover:bg-indigo-500"
                                : "bg-indigo-500/15 text-indigo-100 hover:bg-indigo-500/25")
                            }
                            onClick={() => setPlanProspectId("household")}
                            data-testid="runner-plan-prospect-household"
                          >
                            Household
                          </Button>
                          {(prospects || []).map((p) => (
                            <Button
                              key={p.id}
                              variant="secondary"
                              className={
                                "h-8 rounded-full px-3 " +
                                (planProspectId === p.id
                                  ? "bg-indigo-500/90 text-white hover:bg-indigo-500"
                                  : "bg-indigo-500/15 text-indigo-100 hover:bg-indigo-500/25")
                              }
                              onClick={() => setPlanProspectId(p.id)}
                              data-testid={`runner-plan-prospect-${p.id}`}
                            >
                              {p.name}
                            </Button>
                          ))}
                        </div>

                        {/* Option 1/2/3 toggle removed from Your Reply header; option-specific values are inserted via $(option:N:FIELD_ID) tokens. */}
                      </div>
                    ) : (
                      <div />
                    )}

                    {/* Font Controls (MA-ARCH-162: extracted component) */}
                    <ReaderFontControls
                      showBackButton={nodeHistory.length > 0}
                      onBack={onGoBack}
                      onDecrease={() => setReaderFontDelta((d) => Math.max(-4, d - 1))}
                      onIncrease={() => setReaderFontDelta((d) => Math.min(8, d + 1))}
                    />
                  </div>


                </CardTitle>
              </CardHeader>
              <CardContent
                className="space-y-4 min-h-[360px]"
                data-testid="runner-reply-content"
              >
                <div
                  className="whitespace-pre-wrap rounded-2xl border border-white/10 bg-black/20 p-4 text-slate-100"
                  style={{
                    fontSize: `${Math.max(12, scriptReplyFont + readerFontDelta)}px`,
                  }}
                  data-testid="runner-your-reply-text"
                >
                  {/* toggles moved to card header */}
                  {/* current prospect row rendered above */}
                  {Array.isArray(payload.node.script_blocks) &&
                  payload.node.script_blocks.length > 0 ? (
                    <div className="space-y-4">
                      {payload.node.script_blocks.map((sb) => (
                        <div
                          key={sb.id || JSON.stringify(sb)}
                          className="rounded-2xl border border-white/10 bg-white/5 p-3"
                        >
                          <div className="space-y-3">
                            {(sb.blocks || []).map((b) => (
                              <div key={b.id || JSON.stringify(b)}>
                                {b.type === "text" ? (
                                  <div className="whitespace-pre-wrap">
                                    {renderTextWithTokens((b.text || "").trim()) ||
                                      ((b.text || "").trim() || "(empty)")}
                                  </div>
                                ) : b.type === "field" ? (
                                  <div className="w-full rounded-xl border border-indigo-400/30 bg-indigo-500/10 p-3 md:w-1/2">
                                    {(() => {
                                      const f = (allFields || []).find(
                                        (x) => x.id === b.field_id,
                                      );
                                      if (!f)
                                        return (
                                          <div className="text-sm text-slate-200/70">—</div>
                                        );

                                      // Auto-assign repeated fields inside a Prospect-scoped Script Block.
                                      // 1st occurrence -> Prospect 1, 2nd -> Prospect 2, etc.
                                      const desiredScope = (() => {
                                        if (sb.scope !== "prospect") {
                                          return String(b.prospect_scope || "").trim() || "household";
                                        }

                                        // For Prospect-scoped Script Blocks, prefer the auto index (or per-field occurrence index)
                                        // over any legacy pinned scope.
                                        const autoIdx = Number(b.auto_prospect_index);
                                        if (Number.isFinite(autoIdx) && autoIdx > 0) {
                                          return `prospect_${autoIdx}`;
                                        }

                                        // Fallback: count occurrences of this field within this Script Block.
                                        // This prevents legacy blocks (multiple prospect_1 pins) from rendering incorrectly.
                                        const fid = b.field_id;
                                        if (fid) {
                                          const occ = (sb.blocks || []).filter(
                                            (x) => x?.type === "field" && x?.field_id === fid,
                                          );
                                          const idx = occ.findIndex(
                                            (x) => (x?.id || JSON.stringify(x)) === (b.id || JSON.stringify(b)),
                                          );
                                          if (idx >= 0) return `prospect_${idx + 1}`;
                                        }

                                        return "prospect_1";
                                      })();

                                      const desiredInstanceProspectId =
                                        desiredScope === "prospect_1"
                                          ? prospects?.[0]?.id
                                          : desiredScope === "prospect_2"
                                            ? prospects?.[1]?.id
                                            : desiredScope === "prospect_3"
                                              ? prospects?.[2]?.id
                                              : null;

                                      // If this field is assigned to Prospect N but that prospect doesn't exist in the run,
                                      // do not display the field; instead show an inline "Add Prospect" CTA.
                                      if (
                                        sb.scope === "prospect" &&
                                        desiredScope !== "household" &&
                                        !desiredInstanceProspectId
                                      ) {
                                        return (
                                          <div className="space-y-2">
                                            <div className="text-xs text-slate-200/70">
                                              This field is assigned to {desiredScope.replace("_", " ")}, but that prospect doesn’t exist yet.
                                            </div>
                                            <AddProspectInline onAdd={addProspect} />
                                          </div>
                                        );
                                      }

                                      const instanceProspectId =
                                        desiredInstanceProspectId || "household";

                                      return (
                                        <div className="space-y-2">
                                          {renderFieldInput(
                                            { ...f, scope: desiredScope === "household" ? "household" : "prospects" },
                                            true,
                                            instanceProspectId,
                                          )}
                                        </div>
                                      );
                                    })()}
                                  </div>
                                ) : (
                                  <div className="text-sm text-slate-200/70">—</div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : Array.isArray(payload.node.your_reply_blocks) &&
                    payload.node.your_reply_blocks.length > 0 ? (
                    <div className="space-y-3">
                      {payload.node.your_reply_blocks.map((b) => (
                        <div key={b.id || b._id || JSON.stringify(b)}>
                          {b.type === "text" ? (
                            <div className="whitespace-pre-wrap">
                              {renderTextWithTokens((b.text || "").trim()) ||
                                ((b.text || "").trim() || "(empty)")}
                            </div>
                          ) : b.type === "var" ? (
                            <span className="rounded-md bg-white/10 px-2 py-1 text-slate-100">
                              {(() => {
                                const label = b.label || "";
                                const placeholder = "—";

                                const isPlanOptionsNode =
                                  payload?.node?.scope === "plan_options";

                                const resolveProspectId = () => {
                                  if (isPlanOptionsNode)
                                    return planProspectId || "household";

                                  const scope = b.prospect_scope || "household";
                                  if (scope === "household") return "household";
                                  if (scope === "current")
                                    return planProspectId || "household";
                                  if (scope === "prospect_1")
                                    return prospects?.[0]?.id || "household";
                                  if (scope === "prospect_2")
                                    return (
                                      prospects?.[1]?.id ||
                                      prospects?.[0]?.id ||
                                      "household"
                                    );
                                  if (scope === "prospect_3")
                                    return (
                                      prospects?.[2]?.id ||
                                      prospects?.[0]?.id ||
                                      "household"
                                    );
                                  return "household";
                                };

                                const optionIndex = isPlanOptionsNode
                                  ? planOptionIndex
                                  : b.option_index || 1;

                                // data field
                                if (b.source === "data") {
                                  const f = (allFields || []).find(
                                    (x) => x.id === b.field_id,
                                  );
                                  if (!f) return placeholder;

                                  const subject = resolveProspectId();
                                  const v = fieldValues[`${f.id}::${subject}`];
                                  return v === undefined || v === ""
                                    ? placeholder
                                    : String(v);
                                }

                                // quote input
                                if (b.source === "quote") {
                                  const pid = resolveProspectId();
                                  const key = `${b.field_id}::${pid}::${optionIndex}`;
                                  const v = quoteValues[key];
                                  return v === undefined || v === ""
                                    ? placeholder
                                    : String(v);
                                }

                                // calculated quote field
                                if (b.source === "calculated") {
                                  const cf = (quoteFields || []).find(
                                    (x) => x.id === b.field_id,
                                  );
                                  if (!cf) return placeholder;
                                  const pid = resolveProspectId();
                                  const raw = computeQuoteFormula(
                                    cf.formula || [],
                                    pid,
                                    optionIndex,
                                  );
                                  return !raw || raw === "Error"
                                    ? placeholder
                                    : String(raw);
                                }

                                // fallback
                                return label || placeholder;
                              })()}
                            </span>
                          ) : b.type === "field" ? (
                            <div className="w-full rounded-xl border border-indigo-400/30 bg-indigo-500/10 p-3 md:w-1/2">
                              {(() => {
                                const f = (allFields || []).find(
                                  (x) => x.id === b.field_id,
                                );
                                if (!f)
                                  return (
                                    <div className="text-sm text-slate-200/70">—</div>
                                  );
                                const scope = f.scope || "household";
                                const subject =
                                  scope === "prospects" && prospects?.length
                                    ? prospects[0].id
                                    : "household";

                                return (
                                  <div className="space-y-2">
                                    {renderFieldInput(f, true, subject)}
                                  </div>
                                );
                              })()}
                            </div>
                          ) : (
                            <div className="text-sm text-slate-200/70">—</div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    (payload.node.your_reply || "").trim() || "(empty)"
                  )}
                </div>

                <Collapsible
                  defaultOpen
                  data-testid="runner-coaching-collapsible"
                >
                  <CollapsibleTrigger
                    className="flex w-full items-center justify-between rounded-xl bg-indigo-500/90 px-4 py-3 text-left font-medium text-white hover:bg-indigo-500"
                    style={{
                      fontSize: `${Math.max(12, scriptReplyFont + readerFontDelta)}px`,
                    }}
                    data-testid="runner-coaching-toggle"
                  >
                    Coaching note
                    <ChevronDown className="h-4 w-4 text-slate-200/70" />
                  </CollapsibleTrigger>
                  <CollapsibleContent
                    className="mt-2"
                    data-testid="runner-coaching-content"
                  >
                    <div
                      className="whitespace-pre-wrap rounded-2xl border border-white/10 bg-white/5 p-4 text-slate-200/80"
                      style={{
                        fontSize: `${Math.max(12, scriptReplyFont + readerFontDelta)}px`,
                      }}
                      data-testid="runner-coaching-text"
                    >
                      {(payload.node.coaching_note || "").trim() || "(none)"}
                    </div>
                  </CollapsibleContent>
                </Collapsible>

                {/* Inline Data Fields section removed (fields now inline with script text) */}
              </CardContent>
            </Card>

            <Card className="glass" data-testid="runner-prospect-says-card">
              <CardHeader>
                <div className="flex items-center justify-between gap-3">
                  <CardTitle
                    className="flex items-center gap-2 text-slate-100"
                    data-testid="runner-prospect-says-title"
                  >
                    <MessageSquare className="h-5 w-5 text-indigo-400" />
                    Prospect Says
                  </CardTitle>

                  <div />
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {payload.options.length === 0 ? (
                  <div className="muted" data-testid="runner-no-options-text">
                    No next-step options. Add prospect responses in the Builder.
                  </div>
                ) : (
                  <div
                    className="flex flex-wrap gap-2"
                    data-testid="runner-options-list"
                  >
                    {payload.options.map((o) => (
                      <Button
                        key={o.id}
                        className="rounded-2xl bg-indigo-500/90 text-white hover:bg-indigo-500"
                        onClick={() => onPickOption(o)}
                        data-testid={`runner-option-button-${o.id}`}
                      >
                        {o.prospect_says}
                      </Button>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
            {/* Bottom spacer to ensure consistent scroll landing for auto-scroll */}
            <div className="h-[240px]" aria-hidden />
          </div>

          {/* Quick Reference Card (combined rail + content with divider) */}
          <TooltipProvider>
            <Card
              className={
                "hidden lg:flex flex-col glass h-[calc(100vh-160px)] overflow-hidden rounded-xl transition-all duration-200 justify-self-end w-full " +
                (quickRefCollapsed ? "w-[76px]" : "w-[460px]")
              }
              data-testid="prospect-info-panel"
            >
              <div
                className="relative flex h-full min-h-0 w-full"
                data-testid="quick-reference-shell"
              >
                {/* Content pane (opens to the LEFT of the rail) */}
                <div
                  className={
                    "min-w-0 min-h-0 flex-1 flex flex-col pr-[76px] transition-all duration-200 overflow-hidden " +
                    (quickRefCollapsed
                      ? "w-0 opacity-0 pointer-events-none"
                      : "w-auto opacity-100")
                  }
                  data-testid="quick-reference-content-pane"
                >
                  <CardHeader>
                    <div className="space-y-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="space-y-1">
                          <CardTitle
                            className="text-slate-100"
                            data-testid="prospect-info-title"
                          >
                            {prospectPanelView === "prospect_info"
                              ? "Prospect Info"
                              : prospectPanelView === quoteToolId
                                ? "Quote Sheet"
                                : prospectPanelView === "placeholder_2"
                                  ? "Calculator"
                                  : prospectPanelView === "placeholder_3"
                                    ? "Calendar"
                                    : prospectPanelView === "placeholder_4"
                                      ? "Notes"
                                      : "Coming soon"}
                          </CardTitle>
                          <div
                            className="hidden"
                            data-testid="quick-reference-subtitle"
                          />
                        </div>

                        {prospectPanelView === "placeholder_3" ? (
                          <Button
                            type="button"
                            className="h-8 rounded-full bg-indigo-500/90 px-3 text-sm text-white hover:bg-indigo-500"
                            onClick={() => setSetApptOpen(true)}
                            disabled={!setApptLeadPayload?.id}
                            data-testid="quick-reference-set-appt-button"
                            title={setApptLeadPayload?.id ? "Set appointment" : "Lead not available"}
                          >
                            Set Appt
                          </Button>
                        ) : null}
                      </div>

                      {prospectPanelView === "prospect_info" ? (
                        <ProspectFilterHeader
                          filter={prospectInfoFilter}
                          onFilterChange={setProspectInfoFilter}
                          prospects={prospects}
                          runId={runId}
                          onEditProspects={openProspectEdit}
                        />
                      ) : null}
                    </div>
                  </CardHeader>

                  {prospectPanelView !== "prospect_info" &&
                  prospectPanelView !== quoteToolId &&
                  prospectPanelView !== "placeholder_2" &&
                  prospectPanelView !== "placeholder_3" &&
                  prospectPanelView !== "placeholder_4" ? (
                    <CardContent
                      className="min-h-0 flex-1 overflow-y-auto"
                      data-testid="prospect-panel-placeholder"
                    >
                      <div className="muted">Coming soon</div>
                    </CardContent>
                  ) : prospectPanelView === quoteToolId ? (
                    <CardContent
                      className="min-h-0 flex-1 overflow-y-auto pt-4"
                      data-testid="quick-reference-quote-sheet"
                    >
                      <div className="space-y-5">
                        {/* Prospect selector */}
                        <div className="space-y-3">
                          <div
                            className="flex items-center justify-between gap-2"
                            data-testid="quote-for-row"
                          >
                            <div className="flex w-full items-center gap-2">
                              <span className="text-sm font-medium text-slate-200/80">
                                For:
                              </span>
                              <Select
                                value={quoteProspectId || ""}
                                onValueChange={(v) => setQuoteProspectId(v)}
                              >
                                <SelectTrigger className="h-9 w-full bg-white/5 text-slate-100 ring-1 ring-white/10">
                                  <SelectValue placeholder="Choose…" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="household">
                                    Household
                                  </SelectItem>
                                  {(prospects || []).map((p) => (
                                    <SelectItem key={p.id} value={p.id}>
                                      {p.name}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>

                            <div className="text-xs text-slate-200/70">
                              {quoteAutoSaveStatus === "saving"
                                ? "Saving…"
                                : quoteAutoSaveStatus === "saved"
                                  ? "✓ Saved"
                                  : quoteAutoSaveStatus === "error"
                                    ? "Save failed"
                                    : ""}
                            </div>
                          </div>

                          <UiSeparator className="bg-white/10" />

                          <div
                            className="space-y-2"
                            data-testid="quote-option-row"
                          >
                            <div className="flex justify-center">
                              <div className="flex items-center gap-2 rounded-full bg-white/5 p-1 ring-1 ring-white/10">
                                {[1, 2, 3].map((opt) => (
                                  <Button
                                    key={opt}
                                    variant="secondary"
                                    className={
                                      "h-8 rounded-full px-3 text-sm " +
                                      (quoteOptionIndex === opt
                                        ? "bg-indigo-500/90 text-white hover:bg-indigo-500"
                                        : "bg-indigo-500/15 text-indigo-100 hover:bg-indigo-500/25")
                                    }
                                    onClick={() => setQuoteOptionIndex(opt)}
                                    data-testid={`quote-option-${opt}`}
                                  >
                                    {`Option ${opt}`}
                                  </Button>
                                ))}
                              </div>
                            </div>
                          </div>

                        {/* Inputs */}
                        <div className="space-y-2">
                          <div className="text-sm font-semibold text-slate-100">
                            {quoteProspectId === "household"
                              ? "Calculated"
                              : "Inputs"}
                          </div>
                          <div className="space-y-3">
                            {(quoteInputFields || []).map((f) => {
                                const targetProspectId = quoteSheetStatus.targetProspectId;
                                const key = `${f.id}::${targetProspectId}::${quoteOptionIndex}`;
                                const label = getQuoteFieldLabel(f);
                                const isCompany = isCompanyQuoteField(label);
                                const isProduct = isProductQuoteField(label);
                                const isIssueAge = isIssueAgeQuoteField(label);
                                const resolvedFieldType = isCompany || isProduct || isIssueAge ? "dropdown" : f.field_type;

                                if (!targetProspectId) {
                                  return (
                                    <div
                                      key={f.id}
                                      className="rounded-xl border border-white/10 bg-white/5 p-3 text-sm text-slate-200/70"
                                    >
                                      Add prospects to start entering quote
                                      inputs.
                                    </div>
                                  );
                                }

                                if (
                                  quoteProspectId === "household" &&
                                  f.include_in_household
                                ) {
                                  const sum = (prospects || []).reduce(
                                    (acc, p) => {
                                      const raw =
                                        quoteValues[
                                          `${f.id}::${p.id}::${quoteOptionIndex}`
                                        ] || "";
                                      const n = parseFloat(
                                        String(raw).replace(/,/g, ""),
                                      );
                                      return acc + (Number.isFinite(n) ? n : 0);
                                    },
                                    0,
                                  );
                                  const shown = Number.isFinite(sum)
                                    ? sum.toFixed(2)
                                    : "0.00";

                                  return (
                                    <div
                                      key={f.id}
                                      className="space-y-2 rounded-xl border border-white/10 bg-white/5 p-3"
                                    >
                                      <div className="text-sm font-medium text-slate-100">
                                        {f.label}
                                      </div>
                                      <Input
                                        value={shown}
                                        readOnly
                                        className="bg-black/30 text-slate-100 ring-1 ring-white/10"
                                      />
                                      <div className="text-xs text-slate-200/60">
                                        Household sum (computed)
                                      </div>
                                    </div>
                                  );
                                }

                                const value = quoteValues[key] || "";
                                const companyValue = quoteSheetStatus.companyValue;
                                const productValue = quoteSheetStatus.productValue;
                                const issueAgeOptions = isIssueAge ? getIssueAgeOptions(companyValue, productValue) : [];
                                const dropdownOptions = isCompany
                                  ? companyOptions
                                  : isProduct
                                    ? getProductOptionsByCompanyName(companyValue)
                                    : isIssueAge
                                      ? issueAgeOptions
                                      : f.options || [];
                                const selectPlaceholder = isIssueAge
                                  ? profilesLoading
                                    ? "Loading issue ages..."
                                    : dropdownOptions.length
                                      ? "Select issue age"
                                      : "Select company and product"
                                  : dropdownOptions.length
                                    ? "Choose…"
                                    : "No options";
                                return (
                                  <div
                                    key={f.id}
                                    className="space-y-2 rounded-xl border border-white/10 bg-white/5 p-3"
                                    data-testid={`quote-sheet-input-${f.id}`}
                                  >
                                    <div className="text-sm font-medium text-slate-100">
                                      {f.label}
                                      {isCompany || isProduct || isIssueAge ? (
                                        <span className="ml-2 text-xs text-emerald-200">Required</span>
                                      ) : null}
                                    </div>
                                    {resolvedFieldType === "textarea" ? (
                                      <Textarea
                                        value={value}
                                        onChange={(e) =>
                                          setQuoteValue(
                                            f.id,
                                            targetProspectId,
                                            quoteOptionIndex,
                                            e.target.value,
                                          )
                                        }
                                        className="min-h-[90px] bg-white/5 text-slate-100 ring-1 ring-white/10"
                                        data-testid={`quote-sheet-textarea-${f.id}-${targetProspectId}-${quoteOptionIndex}`}
                                      />
                                    ) : resolvedFieldType === "dropdown" ? (
                                      <Select
                                        value={value}
                                        onValueChange={(v) =>
                                          setQuoteValue(
                                            f.id,
                                            targetProspectId,
                                            quoteOptionIndex,
                                            v,
                                          )
                                        }
                                        disabled={dropdownOptions.length === 0}
                                      >
                                        <SelectTrigger
                                          className="h-9 w-full bg-white/5 text-slate-100 ring-1 ring-white/10"
                                          data-testid={`quote-sheet-select-${f.id}-${targetProspectId}-${quoteOptionIndex}`}
                                        >
                                        <SelectValue placeholder={selectPlaceholder} />
                                        </SelectTrigger>
                                        <SelectContent>
                                          {(dropdownOptions || []).map((opt, idx2) => (
                                            <SelectItem
                                              key={opt}
                                              value={opt}
                                              data-testid={`quote-sheet-select-option-${f.id}-${idx2}`}
                                            >
                                              {opt}
                                            </SelectItem>
                                          ))}
                                        </SelectContent>
                                      </Select>
                                    ) : (
                                      <Input
                                        value={value}
                                        onChange={(e) =>
                                          setQuoteValue(
                                            f.id,
                                            targetProspectId,
                                            quoteOptionIndex,
                                            e.target.value,
                                          )
                                        }
                                        className="bg-white/5 text-slate-100 ring-1 ring-white/10"
                                        inputMode={
                                          resolvedFieldType === "number"
                                            ? "decimal"
                                            : undefined
                                        }
                                        data-testid={`quote-sheet-input-${f.id}-${targetProspectId}-${quoteOptionIndex}`}
                                      />
                                    )}
                                  </div>
                                );
                              })}
                          </div>
                        </div>

                        {quoteProspectId !== "household" && quoteSheetStatus.isReady ? (
                          <>
                            <div className="h-px bg-white/10" aria-hidden />

                            {/* Calculated */}
                            <div className="space-y-2">
                              <div className="text-sm font-semibold text-slate-100">
                                Calculated
                              </div>
                              <div className="space-y-3">
                                {(quoteFields || [])
                                  .filter((f) => f.kind === "calculated")
                                  .filter((f) =>
                                    quoteProspectId === "household"
                                      ? !!f.show_in_household
                                      : true,
                                  )
                                  .sort(
                                    (a, b) =>
                                      (a.sort_order ?? 0) - (b.sort_order ?? 0),
                                  )
                                  .map((f) => {
                                    const targetProspectId =
                                      quoteProspectId === "household"
                                        ? prospects?.[0]?.id || ""
                                        : quoteProspectId;
                                    if (!targetProspectId) {
                                      return (
                                        <div
                                          key={f.id}
                                          className="rounded-xl border border-white/10 bg-white/5 p-3 text-sm text-slate-200/70"
                                        >
                                          Add prospects to see calculated
                                          values.
                                        </div>
                                      );
                                    }

                                    const computedRaw = computeQuoteFormula(
                                      f.formula || [],
                                      targetProspectId,
                                      null,
                                      quoteProspectId === "household" ? "household" : "prospect",
                                    );
                                    const num = parseFloat(
                                      String(computedRaw).replace(/,/g, ""),
                                    );
                                    const shown =
                                      computedRaw === "Error"
                                        ? "Error"
                                        : Number.isFinite(num)
                                          ? num.toFixed(2)
                                          : "0.00";

                                    return (
                                      <div
                                        key={f.id}
                                        className="space-y-2 rounded-xl border border-white/10 bg-white/5 p-3"
                                      >
                                        <div className="text-sm font-medium text-slate-100">
                                          {f.label}
                                        </div>
                                        <Input
                                          value={shown}
                                          readOnly
                                          className="bg-black/30 text-slate-100 ring-1 ring-white/10"
                                        />
                                      </div>
                                    );
                                  })}
                              </div>
                            </div>
                          </>
                        ) : null}
                      </div>
                      </div>
                    </CardContent>
                  ) : prospectPanelView === "placeholder_2" ? (
                    /* Calculator Panel (MA-ARCH-161: extracted component) */
                    <CalculatorPanel />
                  ) : prospectPanelView === "placeholder_3" ? (
                    /* Calendar Panel (MA-ARCH-161: extracted component) */
                    <CalendarPanel
                      selected={calendarSelected}
                      onSelect={setCalendarSelected}
                    />
                  ) : prospectPanelView === "placeholder_4" ? (
                    /* Notes Panel (MA-ARCH-161: extracted component) */
                    <NotesPanel
                      value={notesText}
                      onChange={setNotesText}
                    />
                  ) : (
                    <CardContent
                      className="min-h-0 flex-1 overflow-y-auto pt-4"
                      data-testid="prospect-info-content"
                    >
                      {uniqueFields.length === 0 ? (
                        <div
                          className="muted"
                          data-testid="no-prospect-fields-text"
                        >
                          No data fields configured. Add fields in the Script
                          Builder.
                        </div>
                      ) : (
                        <div
                          className="space-y-4"
                          data-testid="prospect-fields-list"
                        >
                          {uniqueFields.flatMap((f) => {
                            const scope = f.scope || "household";

                            // Household field: only show when filter is All or Household
                            if (scope !== "prospects") {
                              if (
                                prospectInfoFilter === "all" ||
                                prospectInfoFilter === "household"
                              ) {
                                return [renderFieldInput(f, false, null)];
                              }
                              // Filter is a specific prospect -> hide household fields (per your requirement)
                              return [];
                            }

                            // Prospects-scoped fields: render one instance per prospect
                            if (!prospects || prospects.length === 0) {
                              // No prospects yet; nothing to render
                              return [];
                            }

                            return prospects
                              .map((p) => {
                                const base = renderFieldInput(f, false, p.id);
                                if (!base) return null;

                                return (
                                  <div
                                    key={`${f.id}::${p.id}`}
                                    className="space-y-1"
                                  >
                                    <div className="flex items-center justify-end">
                                      <Badge className="bg-white/10 text-slate-100 ring-1 ring-white/10">
                                        Prospect: {p.name}
                                      </Badge>
                                    </div>
                                    {base}
                                  </div>
                                );
                              })
                              .filter(Boolean);
                          })}
                        </div>
                      )}
                    </CardContent>
                  )}
                </div>

                {/* Icon Rail (MA-ARCH-162: extracted component) */}
                <QuickReferenceRail
                  collapsed={prospectPanelCollapsed}
                  currentView={prospectPanelView}
                  quoteToolId={quoteToolId}
                  onToggleCollapse={() => setProspectPanelCollapsed((v) => !v)}
                  onViewChange={(viewId) => {
                    setProspectPanelView(viewId);
                    if (viewId !== "prospect_info") {
                      setProspectPanelCollapsed(false);
                    }
                  }}
                />

                {/* (removed duplicate content pane block) */}
              </div>
            </Card>
          </TooltipProvider>
        </div>
      )}
    </div>
  );
}
